// src/pages/PropertyDetailsPage.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation, Link as RouterLink } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import PurchaseOrdersPanel from "./finance/PurchaseOrdersPanel";
import { hasFullPropertyAccess, hasPurchaseOrderAccess, hasFinanceAccess, hasPropertyWriteAccess } from "../utils/roleAccess";
import { useBreadcrumb } from "../context/BreadcrumbContext";
import { SYSTEM_NAME } from "../config";
import { formatDateUk } from "../utils/dateDisplay";
import Layout from "../components/layout/Layout";
import { getAssessmentsPage } from "../api/assessmentApi";

import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Chip,
  FormControlLabel,
  Switch,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Collapse,
  Alert,
  Skeleton,
  Snackbar,
  Tooltip,
  IconButton,
  TextField,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  Autocomplete,
  Tabs,
  Tab,
  Stack,
  Link,
  FormControl,
  InputLabel,
  Radio,
  RadioGroup,
  Select,
  MenuItem,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorIcon from "@mui/icons-material/Error";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";

import {
  getPropertyDetails,
  getPropertyDirectors,
  createPropertyDirector,
  updatePropertyDirector,
  deletePropertyDirector,
  getDirectorsEmailLink,
  getAssessmentTypes,
  getPropertyLPE1Forms,
  lookupAddress,
  getAddressById,
  updateProperty,
  updatePropertyMetadata,
} from "../api/propertyApi";
import {
  importUnits,
  previewImportUnits,
  downloadUnitsExcel,
  deleteUnit,
  getUnitsByProperty,
  getUnitsEmailLink,
  getUnitGroups,
  createUnitGroup,
  getUnitGroupUnits,
  getUnitGroupAvailableUnits,
  addUnitsToGroup,
  getGroupUnitsEmailLink,
  removeUnitsFromGroup,
  deleteUnitGroup,
} from "../api/unitApi";
import { getTickets, getTicketComments, createTicket } from "../api/ticketApi";
import {
  createComplianceSchedule,
  createCommunalFireDoor,
  getComplianceTypes,
  getPropertyCompliance,
  createComplianceTask,
  updateComplianceTask,
  deleteComplianceTask,
} from "../api/complianceApi";
import { getApiErrorMessage } from "../utils/concurrency";

const formatPreviewValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return value;
};

// Helper to get document status based on expiry/renewal date
const getDocumentStatus = (dateStr) => {
  if (!dateStr) return { status: "unknown", label: "No date", color: "default" };
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiryDate = new Date(dateStr);
  expiryDate.setHours(0, 0, 0, 0);
  
  const diffDays = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
  
  if (diffDays < 0) {
    return { status: "expired", label: "Expired", color: "error", icon: <ErrorIcon fontSize="small" /> };
  } else if (diffDays <= 90) {
    return { status: "expiring", label: `${diffDays}d left`, color: "warning", icon: <WarningAmberIcon fontSize="small" /> };
  } else {
    return { status: "valid", label: "Valid", color: "success", icon: <CheckCircleIcon fontSize="small" /> };
  }
};

const getMailtoRecipientSeparator = () => {
  if (typeof navigator === "undefined") return ";";

  const appleDevice = /Mac|iPhone|iPad|iPod/i.test(
    `${navigator.platform || ""} ${navigator.userAgent || ""}`
  );
  return appleDevice ? "," : ";";
};

const formatMailto = (emails, separator = getMailtoRecipientSeparator()) =>
  (emails || "")
    .split(/[;,]+/)
    .map((email) => email.trim())
    .filter(Boolean)
    .join(separator);

const normalizeMailtoUrl = (mailtoUrl) => {
  if (!mailtoUrl || !mailtoUrl.startsWith("mailto:")) {
    return mailtoUrl;
  }
  const raw = mailtoUrl.slice("mailto:".length);
  const [toPart, query] = raw.split("?");
  const normalized = formatMailto(toPart);
  const normalizedQuery = query
    ? query
        .split("&")
        .map((part) => {
          const separatorIndex = part.indexOf("=");
          if (separatorIndex === -1) return part;

          const key = part.slice(0, separatorIndex);
          const value = part.slice(separatorIndex + 1);
          if (key.toLowerCase() !== "bcc") return part;

          let decodedValue = value;
          try {
            decodedValue = decodeURIComponent(value);
          } catch {
            // Keep malformed API values usable rather than blocking the mail client.
          }
          return `${key}=${formatMailto(decodedValue)}`;
        })
        .join("&")
    : "";
  return `mailto:${normalized}${normalizedQuery ? `?${normalizedQuery}` : ""}`;
};

const MONTH_LABELS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const getInsuranceSummaryLabel = (policy) => {
  const coverage = (policy?.coverage_type || "").trim();
  if (coverage) {
    return /insurance/i.test(coverage) ? coverage : `${coverage} Insurance`;
  }
  const provider = (policy?.provider_name || "").trim();
  return provider || "Insurance";
};

const getAssessmentSortTimestamp = (assessment) => {
  const primaryDate =
    assessment?.assessment_date ||
    assessment?.expiry_date ||
    null;
  if (!primaryDate) return Number.NEGATIVE_INFINITY;
  const parsed = new Date(primaryDate).getTime();
  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
};

const TAB_QUERY_BY_INDEX = [
  "details",
  "tickets",
  "insurance",
  "utilities",
  "assessments",
  "enquiries",
  "compliance",
  "notes",
  "finance",
  "purchase-orders",
];

const TAB_INDEX_BY_QUERY = TAB_QUERY_BY_INDEX.reduce((acc, key, index) => {
  acc[key] = index;
  return acc;
}, {});

const FIRE_DOOR_QUESTION_FIELDS = [
  { key: "is_fire_door", label: "Is it a fire door?" },
  { key: "frame_gaps_under_4mm", label: "Frame gaps under 4mm? (top and sides)" },
  { key: "threshold_gap_under_9mm", label: "Threshold gap under 9mm? (bottom)" },
  { key: "letterbox_has_intumescent_seals", label: "Letterbox, if any, has intumescent seals?" },
  { key: "intumescent_strips_fitted", label: "Intumescent strips fitted and suitable?" },
  { key: "three_hinges_present", label: "Three hinges present?" },
  { key: "all_hinge_screws_present", label: "All hinge screws present?" },
  { key: "self_close_device_fitted", label: "Self-close device fitted?" },
  { key: "self_close_action_effective", label: "Self-close action effective?" },
];

function PropertyDetailsPage() {
  const theme = useTheme();
  const checklistDialogFullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, permissions, roleCapabilities } = useAuth();
  const { setLabel, clearLabel } = useBreadcrumb();
  const UNITS_PAGE_SIZE = 10;
  const TICKETS_PAGE_SIZE = 10;
  const COMPLIANCE_PAGE_SIZE = 10;
  const FIRE_DOOR_PAGE_SIZE = 10;
  const ASSESSMENTS_PAGE_SIZE = 20;
  const PROPERTY_ENQUIRIES_PAGE_SIZE = 10;
  const GROUP_UNITS_PAGE_SIZE = 6;
  const GROUP_PICKER_PAGE_SIZE = 8;
  const searchParams = new URLSearchParams(location.search);
  const unitsMissingEmailOnly =
    searchParams.get("units_filter") === "missing-email";
  const requestedTab = searchParams.get("tab");
  const requestedSummary = searchParams.get("summary");
  const userRoles = user?.claims_role || user?.roles || [];
  const canAccessFinance = hasFinanceAccess(userRoles, roleCapabilities);
  const canAccessPurchaseOrders = hasPurchaseOrderAccess(userRoles, roleCapabilities) &&
    (hasFullPropertyAccess(userRoles, roleCapabilities) || (user?.claims_prop || []).some((id) => String(id) === String(propertyId)));

  const [property, setProperty] = useState(null);
  const [assessmentTypes, setAssessmentTypes] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [assessmentsPage, setAssessmentsPage] = useState(1);
  const [assessmentsTotal, setAssessmentsTotal] = useState(0);
  const [assessmentsLoading, setAssessmentsLoading] = useState(false);
  const [assessmentsError, setAssessmentsError] = useState("");
  const [lpe1Forms, setLpe1Forms] = useState([]);
  const [lpe1UnitIds, setLpe1UnitIds] = useState([]);
  const [lpe1Page, setLpe1Page] = useState(1);
  const [lpe1Total, setLpe1Total] = useState(0);
  const [lpe1Loading, setLpe1Loading] = useState(false);
  const [lpe1Error, setLpe1Error] = useState("");
  const [units, setUnits] = useState([]);
  const [unitsPage, setUnitsPage] = useState(1);
  const [unitsTotal, setUnitsTotal] = useState(0);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [unitsSearch, setUnitsSearch] = useState("");
  const [unitsSearchDraft, setUnitsSearchDraft] = useState("");
  const [unitsSearchOpen, setUnitsSearchOpen] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [ticketsPage, setTicketsPage] = useState(1);
  const [ticketsTotal, setTicketsTotal] = useState(0);
  const [ticketsError, setTicketsError] = useState("");
  const [showResolvedClosed, setShowResolvedClosed] = useState(false);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketCreateOpen, setTicketCreateOpen] = useState(false);
  const [ticketCreateSubmitting, setTicketCreateSubmitting] = useState(false);
  const [ticketCreateError, setTicketCreateError] = useState("");
  const [ticketCreateForm, setTicketCreateForm] = useState({
    title: "",
    description: "",
    priority: "Medium",
    type: "",
    requester_name: user?.name || user?.claims?.name || "",
  });
  const [ticketCreatePropertyEnquiryForm, setTicketCreatePropertyEnquiryForm] = useState(null);
  const [ticketCreatePropertyEnquiryOptions, setTicketCreatePropertyEnquiryOptions] = useState([]);
  const [ticketCreatePropertyEnquiryLoading, setTicketCreatePropertyEnquiryLoading] = useState(false);
  const [importPreviewOpen, setImportPreviewOpen] = useState(false);
  const [importRows, setImportRows] = useState([]);
  const [importFile, setImportFile] = useState(null);
  const [importSubmitting, setImportSubmitting] = useState(false);
  const [importErrorCount, setImportErrorCount] = useState(0);
  const [importPreviewError, setImportPreviewError] = useState("");
  const [unitGroups, setUnitGroups] = useState([]);
  const [unitGroupsLoading, setUnitGroupsLoading] = useState(false);
  const [expandedGroupId, setExpandedGroupId] = useState(null);
  const [groupUnitsById, setGroupUnitsById] = useState({});
  const [groupUnitsTotalById, setGroupUnitsTotalById] = useState({});
  const [groupUnitsPageById, setGroupUnitsPageById] = useState({});
  const [groupUnitsLoadingById, setGroupUnitsLoadingById] = useState({});
  const [groupCreateOpen, setGroupCreateOpen] = useState(false);
  const [groupCreateName, setGroupCreateName] = useState("");
  const [groupCreateSubmitting, setGroupCreateSubmitting] = useState(false);
  const [groupPickerOpen, setGroupPickerOpen] = useState(false);
  const [groupPickerGroup, setGroupPickerGroup] = useState(null);
  const [groupPickerUnits, setGroupPickerUnits] = useState([]);
  const [groupPickerTotal, setGroupPickerTotal] = useState(0);
  const [groupPickerPage, setGroupPickerPage] = useState(1);
  const [groupPickerSearch, setGroupPickerSearch] = useState("");
  const [groupPickerSelected, setGroupPickerSelected] = useState([]);
  const [groupPickerLoading, setGroupPickerLoading] = useState(false);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [locationPostcode, setLocationPostcode] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [locationResults, setLocationResults] = useState([]);
  const [locationSelected, setLocationSelected] = useState("");
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [notesDraft, setNotesDraft] = useState("");
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesError, setNotesError] = useState("");
  const [directors, setDirectors] = useState([]);
  const [directorDialogOpen, setDirectorDialogOpen] = useState(false);
  const [directorSubmitting, setDirectorSubmitting] = useState(false);
  const [editingDirector, setEditingDirector] = useState(null);
  const [directorForm, setDirectorForm] = useState({
    name: "",
    email: "",
    phone: "",
  });
  const [complianceTasks, setComplianceTasks] = useState([]);
  const [complianceSchedules, setComplianceSchedules] = useState([]);
  const [complianceTypes, setComplianceTypes] = useState([]);
  const [communalFireDoors, setCommunalFireDoors] = useState([]);
  const [communalFireDoorOptions, setCommunalFireDoorOptions] = useState([]);
  const [communalFireDoorPage, setCommunalFireDoorPage] = useState(1);
  const [communalFireDoorTotal, setCommunalFireDoorTotal] = useState(0);
  const [compliancePage, setCompliancePage] = useState(1);
  const [complianceTotal, setComplianceTotal] = useState(0);
  const [complianceLoading, setComplianceLoading] = useState(false);
  const [complianceError, setComplianceError] = useState("");
  const [complianceAlertScope, setComplianceAlertScope] = useState("due_soon");
  const [complianceTypeFilter, setComplianceTypeFilter] = useState("all");
  const [complianceCreateOpen, setComplianceCreateOpen] = useState(false);
  const [complianceCreateSubmitting, setComplianceCreateSubmitting] = useState(false);
  const [complianceCreateChecklistOpen, setComplianceCreateChecklistOpen] = useState(false);
  const [complianceCreateChecklistDialogOpen, setComplianceCreateChecklistDialogOpen] =
    useState(false);
  const [complianceScheduleOpen, setComplianceScheduleOpen] = useState(false);
  const [complianceScheduleSubmitting, setComplianceScheduleSubmitting] = useState(false);
  const [communalFireDoorOpen, setCommunalFireDoorOpen] = useState(false);
  const [communalFireDoorSubmitting, setCommunalFireDoorSubmitting] = useState(false);
  const [complianceScheduleForm, setComplianceScheduleForm] = useState({
    compliance_type_id: "",
    frequency: "",
  });
  const [communalFireDoorForm, setCommunalFireDoorForm] = useState({
    building_name: "",
    floor: "",
    door_type: "communal",
    identification: "",
  });
  const [complianceCreateForm, setComplianceCreateForm] = useState({
    schedule_id: "",
    date_inspected: "",
    result: "",
    completed_by_name: "",
    fail_notes: "",
    document: null,
    fire_door_checklist: [],
  });
  const [complianceEditOpen, setComplianceEditOpen] = useState(false);
  const [complianceEditSubmitting, setComplianceEditSubmitting] = useState(false);
  const [complianceEditChecklistOpen, setComplianceEditChecklistOpen] = useState(false);
  const [complianceEditChecklistDialogOpen, setComplianceEditChecklistDialogOpen] =
    useState(false);
  const [complianceViewChecklistDialogOpen, setComplianceViewChecklistDialogOpen] =
    useState(false);
  const [complianceViewChecklistRows, setComplianceViewChecklistRows] = useState([]);
  const [complianceViewChecklistTask, setComplianceViewChecklistTask] = useState(null);
  const [complianceEditForm, setComplianceEditForm] = useState({
    compliance_type_id: "",
    frequency: "",
    due_date: "",
    date_inspected: "",
    result: "",
    fail_notes: "",
    document: null,
    fire_door_checklist: [],
  });
  const [complianceSelectedTask, setComplianceSelectedTask] = useState(null);
  const [pendingComplianceScheduleScrollId, setPendingComplianceScheduleScrollId] =
    useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Toast state
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    setAssessmentsPage(1);
  }, [propertyId]);

  useEffect(() => {
    setLpe1Page(1);
  }, [propertyId]);

  useEffect(() => {
    const loadAssessments = async () => {
      try {
        setAssessmentsLoading(true);
        setAssessmentsError("");
        const data = await getAssessmentsPage({
          propertyId,
          page: assessmentsPage,
          pageSize: ASSESSMENTS_PAGE_SIZE,
        });
        setAssessments(data?.results || []);
        setAssessmentsTotal(data?.total || 0);
        if (data?.page && data.page !== assessmentsPage) {
          setAssessmentsPage(data.page);
        }
      } catch (err) {
        console.error("Failed to load property assessments:", err);
        setAssessmentsError("Failed to load assessments.");
      } finally {
        setAssessmentsLoading(false);
      }
    };

    if (propertyId) {
      loadAssessments();
    }
  }, [propertyId, assessmentsPage]);

  useEffect(() => {
    const loadLpe1Forms = async () => {
      try {
        setLpe1Loading(true);
        setLpe1Error("");
        const data = await getPropertyLPE1Forms(propertyId, {
          page: lpe1Page,
          page_size: PROPERTY_ENQUIRIES_PAGE_SIZE,
        });
        setLpe1Forms(data?.results || []);
        setLpe1UnitIds(data?.enquiry_unit_ids || data?.lpe1_unit_ids || []);
        setLpe1Total(data?.total || 0);
        if (data?.page && data.page !== lpe1Page) {
          setLpe1Page(data.page);
        }
      } catch (err) {
        console.error("Failed to load property enquiries:", err);
        setLpe1Error("Failed to load property enquiries.");
      } finally {
        setLpe1Loading(false);
      }
    };

    if (propertyId) {
      loadLpe1Forms();
    }
  }, [propertyId, lpe1Page]);

  const showToast = useCallback((message, severity = "success") => {
    setToast({ open: true, message, severity });
  }, []);

  const navigateToTab = useCallback((tabIndex) => {
    const nextTabQuery = TAB_QUERY_BY_INDEX[tabIndex] || TAB_QUERY_BY_INDEX[0];
    if (nextTabQuery === "finance") {
      navigate(`/properties/${propertyId}/finance?tab=overview`);
      return;
    }
    const params = new URLSearchParams(location.search);

    if (nextTabQuery === "details") {
      params.delete("tab");
    } else {
      params.set("tab", nextTabQuery);
    }

    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : "",
      },
      { replace: false }
    );
  }, [location.pathname, location.search, navigate, propertyId]);

  const financialYearStartLabel =
    property?.financial_year_start_day && property?.financial_year_start_month
      ? `${String(property.financial_year_start_day).padStart(2, "0")} ${MONTH_LABELS[property.financial_year_start_month - 1]}`
      : "Not set";

  const COMPLIANCE_TYPE_OPTIONS = complianceTypes.map((item) => ({
    value: String(item.id),
    label: item.name,
    requiresFireDoorChecklist: Boolean(item.requires_fire_door_checklist),
  }));
  const selectedComplianceCreateSchedule = complianceSchedules.find(
    (schedule) => String(schedule.id) === String(complianceCreateForm.schedule_id)
  );
  const createChecklistEnabled =
    Boolean(selectedComplianceCreateSchedule?.requires_fire_door_checklist);
  const editChecklistEnabled =
    COMPLIANCE_TYPE_OPTIONS.find(
      (option) => option.value === String(complianceEditForm.compliance_type_id)
    )?.requiresFireDoorChecklist === true;
  const getComplianceTypeLabel = useCallback(
    (complianceTypeId, fallbackName = "") =>
      COMPLIANCE_TYPE_OPTIONS.find((option) => option.value === String(complianceTypeId))?.label ||
      fallbackName,
    [COMPLIANCE_TYPE_OPTIONS]
  );
  const isCommunalFireDoorTask = useCallback(
    (task) =>
      COMPLIANCE_TYPE_OPTIONS.find(
        (option) => option.value === String(task?.compliance_type_id)
      )?.requiresFireDoorChecklist === true,
    [COMPLIANCE_TYPE_OPTIONS]
  );

  const COMPLIANCE_FREQUENCY_OPTIONS = [
    { value: "monthly", label: "Monthly" },
    { value: "quarterly", label: "Quarterly" },
    { value: "half_yearly", label: "Half Yearly" },
    { value: "annual", label: "Annual" },
  ];
  const FIRE_DOOR_TYPE_OPTIONS = [
    { value: "communal", label: "Communal" },
    { value: "riser", label: "Riser" },
    { value: "flat", label: "Flat" },
  ];
  const TICKET_PRIORITY_OPTIONS = ["Low", "Medium", "High", "Urgent"];
  const TICKET_TYPE_OPTIONS = ["Maintenance", "Major Works", "Management Pack"];

  const normalizeFireDoorChecklist = useCallback(
    (existingItems = []) =>
      existingItems.map((existingItem) => ({
        fire_door_id: existingItem.fire_door_id,
        is_fire_door: existingItem?.is_fire_door ?? true,
        frame_gaps_under_4mm: existingItem?.frame_gaps_under_4mm ?? true,
        threshold_gap_under_9mm: existingItem?.threshold_gap_under_9mm ?? true,
        letterbox_has_intumescent_seals:
          existingItem?.letterbox_has_intumescent_seals ?? true,
        intumescent_strips_fitted: existingItem?.intumescent_strips_fitted ?? true,
        three_hinges_present: existingItem?.three_hinges_present ?? true,
        all_hinge_screws_present: existingItem?.all_hinge_screws_present ?? true,
        self_close_device_fitted: existingItem?.self_close_device_fitted ?? true,
        self_close_action_effective: existingItem?.self_close_action_effective ?? true,
        comments_action: existingItem?.comments_action || "",
      })),
    []
  );

  const buildDefaultFireDoorChecklist = useCallback(
    (existingItems = []) =>
      communalFireDoorOptions.map((fireDoor) => {
        const existingItem = existingItems.find(
          (item) => String(item.fire_door_id) === String(fireDoor.id)
        );
        return {
          fire_door_id: fireDoor.id,
          is_fire_door: existingItem?.is_fire_door ?? true,
          frame_gaps_under_4mm: existingItem?.frame_gaps_under_4mm ?? true,
          threshold_gap_under_9mm: existingItem?.threshold_gap_under_9mm ?? true,
          letterbox_has_intumescent_seals:
            existingItem?.letterbox_has_intumescent_seals ?? true,
          intumescent_strips_fitted: existingItem?.intumescent_strips_fitted ?? true,
          three_hinges_present: existingItem?.three_hinges_present ?? true,
          all_hinge_screws_present: existingItem?.all_hinge_screws_present ?? true,
          self_close_device_fitted: existingItem?.self_close_device_fitted ?? true,
          self_close_action_effective: existingItem?.self_close_action_effective ?? true,
          comments_action: existingItem?.comments_action || "",
        };
      }),
    [communalFireDoorOptions]
  );

  const updateChecklistRow = useCallback((setter, fireDoorId, field, value) => {
    setter((prev) => {
      if (Array.isArray(prev)) {
        return prev.map((row) =>
          String(row.fire_door_id) === String(fireDoorId)
            ? { ...row, [field]: value }
            : row
        );
      }

      return {
        ...prev,
        fire_door_checklist: prev.fire_door_checklist.map((row) =>
          String(row.fire_door_id) === String(fireDoorId)
            ? { ...row, [field]: value }
            : row
        ),
      };
    });
  }, []);

  const renderFireDoorChecklist = useCallback((rows, setter, disabled) => {
    if (!communalFireDoorOptions.length) {
      return (
        <Alert severity="info">
          Add communal fire doors for this property before completing the checklist.
        </Alert>
      );
    }

    return (
      <Box sx={{ mt: 1 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          Fire Door Checklist
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Record the condition of each configured fire door for this inspection.
        </Typography>
        <TableContainer sx={{ border: "1px solid #e0e0e0", borderRadius: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Floor</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Building Number/Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Door Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Identification</TableCell>
                {FIRE_DOOR_QUESTION_FIELDS.map((question) => (
                  <TableCell key={question.key} sx={{ fontWeight: 600, minWidth: 150 }}>
                    {question.label}
                  </TableCell>
                ))}
                <TableCell sx={{ fontWeight: 600, minWidth: 220 }}>
                  Comments and action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const fireDoor = communalFireDoorOptions.find(
                  (item) => String(item.id) === String(row.fire_door_id)
                );
                if (!fireDoor) {
                  return null;
                }

                return (
                  <TableRow key={row.fire_door_id}>
                    <TableCell>{fireDoor.floor}</TableCell>
                    <TableCell>{fireDoor.building_name}</TableCell>
                    <TableCell>{fireDoor.door_type_label}</TableCell>
                    <TableCell>{fireDoor.identification}</TableCell>
                    {FIRE_DOOR_QUESTION_FIELDS.map((question) => (
                      <TableCell key={question.key}>
                        <RadioGroup
                          row
                          value={row[question.key] ? "yes" : "no"}
                          onChange={(event) =>
                            updateChecklistRow(
                              setter,
                              row.fire_door_id,
                              question.key,
                              event.target.value === "yes"
                            )
                          }
                        >
                          <FormControlLabel
                            value="yes"
                            control={<Radio size="small" />}
                            label="Yes"
                            disabled={disabled}
                          />
                          <FormControlLabel
                            value="no"
                            control={<Radio size="small" />}
                            label="No"
                            disabled={disabled}
                          />
                        </RadioGroup>
                      </TableCell>
                    ))}
                    <TableCell>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        value={row.comments_action}
                        onChange={(event) =>
                          updateChecklistRow(
                            setter,
                            row.fire_door_id,
                            "comments_action",
                            event.target.value
                          )
                        }
                        disabled={disabled}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }, [communalFireDoorOptions, updateChecklistRow]);

  const renderChecklistStatus = useCallback((enabled, included, rows) => {
    if (!enabled) {
      return null;
    }

    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
        {included
          ? `Checklist added for ${rows.length} fire door${rows.length === 1 ? "" : "s"}.`
          : "No checklist attached to this compliance task yet."}
      </Typography>
    );
  }, []);

  const getAlertStatus = (dueDate) => {
    if (!dueDate) return { label: "No date", color: "default" };
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    const diffDays = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) {
      return { label: `Overdue ${Math.abs(diffDays)}d`, color: "error" };
    }
    if (diffDays <= 7) {
      return { label: `${diffDays}d left`, color: "warning" };
    }
    return { label: `Due in ${diffDays}d`, color: "success" };
  };

  const getComplianceFrequencyLabel = (frequency) =>
    COMPLIANCE_FREQUENCY_OPTIONS.find((option) => option.value === frequency)?.label ||
    frequency ||
    "-";

  const latestPassedAssessmentByType = useMemo(() => {
    const latestByType = new Map();
    (property?.assessments || []).forEach((assessment) => {
      if (!assessment?.passed || !assessment?.assessment_type_id) return;
      const currentTimestamp = getAssessmentSortTimestamp(assessment);
      const existing = latestByType.get(assessment.assessment_type_id);
      if (!existing) {
        latestByType.set(assessment.assessment_type_id, assessment);
        return;
      }
      const existingTimestamp = getAssessmentSortTimestamp(existing);
      if (
        currentTimestamp > existingTimestamp ||
        (currentTimestamp === existingTimestamp && (assessment.id || 0) > (existing.id || 0))
      ) {
        latestByType.set(assessment.assessment_type_id, assessment);
      }
    });
    return latestByType;
  }, [property?.assessments]);

  const getAssessmentStatus = useCallback((assessment) => {
    const latestPassed = latestPassedAssessmentByType.get(assessment?.assessment_type_id);
    if (
      latestPassed &&
      latestPassed.id !== assessment?.id &&
      (
        getAssessmentSortTimestamp(latestPassed) > getAssessmentSortTimestamp(assessment) ||
        (
          getAssessmentSortTimestamp(latestPassed) === getAssessmentSortTimestamp(assessment) &&
          (latestPassed.id || 0) > (assessment?.id || 0)
        )
      )
    ) {
      return { status: "superseded", label: "Superseded", color: "default" };
    }

    return getDocumentStatus(assessment?.expiry_date);
  }, [latestPassedAssessmentByType]);

  const getScheduleUrgency = (schedule) => {
    if (!schedule?.task_count) {
      return { label: "Pending", color: "error" };
    }
    if (schedule?.latest_result === "Fail") {
      return { label: "Failed", color: "error" };
    }
    if (!schedule?.next_due_date) {
      return { label: "Pending", color: "error" };
    }
    return getAlertStatus(schedule.next_due_date);
  };

  const complianceAttentionItems = useMemo(() => {
    const items = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    (property?.insurance_policies || []).forEach((policy) => {
      if (!policy?.renewal_date) return;
      const renewalDate = new Date(policy.renewal_date);
      renewalDate.setHours(0, 0, 0, 0);
      if (renewalDate < today) {
        items.push({
          key: `insurance-${policy.id}`,
          label: `${getInsuranceSummaryLabel(policy)} expired`,
          target: {
            type: "route",
            href: `/properties/${propertyId}/insurance/${policy.id}/edit`,
          },
        });
      }
    });

    (property?.assessments || []).forEach((assessment) => {
      const status = getAssessmentStatus(assessment);
      if (status.status === "expired") {
        const assessmentType =
          assessmentTypes.find((type) => type.id === assessment.assessment_type_id)?.name ||
          "Assessment";
        items.push({
          key: `assessment-${assessment.id}`,
          label: `${assessmentType} expired`,
          target: {
            type: "route",
            href: `/properties/${propertyId}/assessments/${assessment.id}/edit`,
          },
        });
      }
    });

    complianceSchedules.forEach((schedule) => {
      const typeLabel =
        schedule.compliance_type_name ||
        COMPLIANCE_TYPE_OPTIONS.find(
          (option) => option.value === String(schedule.compliance_type_id)
        )?.label ||
        "Compliance";

      if (schedule.latest_result === "Fail") {
        items.push({
          key: `schedule-fail-${schedule.id}`,
          label: `${typeLabel} Failed`,
          target: { type: "complianceSchedule", scheduleId: schedule.id },
        });
        return;
      }

      if (!schedule.next_due_date) {
        return;
      }

      const nextDueDate = new Date(schedule.next_due_date);
      nextDueDate.setHours(0, 0, 0, 0);
      if (nextDueDate < today) {
        items.push({
          key: `schedule-overdue-${schedule.id}`,
          label: `${typeLabel} Overdue`,
          target: { type: "complianceSchedule", scheduleId: schedule.id },
        });
      }
    });

    return items;
  }, [
    COMPLIANCE_TYPE_OPTIONS,
    assessmentTypes,
    complianceSchedules,
    getAssessmentStatus,
    property?.assessments,
    property?.insurance_policies,
    propertyId,
  ]);

  useEffect(() => {
    const incomingToast = location.state?.toast;
    if (!incomingToast?.message) return;
    showToast(incomingToast.message, incomingToast.severity || "success");
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, navigate, showToast]);

  useEffect(() => {
    if (!property?.name) {
      return;
    }

    document.title = `${property.name} | ${SYSTEM_NAME}`;
  }, [property?.name]);

  const canEdit = hasPropertyWriteAccess(userRoles, permissions);
  const canCreate = userRoles.some((r) =>
    permissions?.[r?.toLowerCase?.()]?.includes("POST")
  );
  const canDelete = userRoles.some((r) =>
    permissions?.[r?.toLowerCase?.()]?.includes("DELETE")
  );
  const propertyActionsDisabled = property?.live === false;

  const isDisabledPropertyAction = () => {
    if (!propertyActionsDisabled) return false;
    showToast("This property is not live.", "warning");
    return true;
  };

  const loadTickets = useCallback(async (page = ticketsPage) => {
    if (!propertyId) return;
    setTicketsLoading(true);
    setTicketsError("");
    try {
      const ticketsData = await getTickets({
        page,
        page_size: TICKETS_PAGE_SIZE,
        sort: "created_at_utc",
        dir: "desc",
        property_id: propertyId,
        show_resolved: showResolvedClosed,
      });
      const results = ticketsData?.results || [];
      const total = ticketsData?.total || 0;
      setTickets(results);
      setTicketsTotal(total);
      if (ticketsData?.page && ticketsData.page !== page) {
        setTicketsPage(ticketsData.page);
      }
    } catch (ticketErr) {
      console.error(ticketErr);
      setTicketsError("Failed to load tickets.");
    } finally {
      setTicketsLoading(false);
    }
  }, [propertyId, showResolvedClosed, ticketsPage]);

  const loadCompliance = useCallback(async (page = compliancePage) => {
    if (!propertyId) return;
    setComplianceLoading(true);
    setComplianceError("");
    try {
      const params = {
        page,
        page_size: COMPLIANCE_PAGE_SIZE,
        fire_door_page: communalFireDoorPage,
        fire_door_page_size: FIRE_DOOR_PAGE_SIZE,
        alerts: complianceAlertScope,
      };
      if (complianceTypeFilter !== "all") {
        params.type = complianceTypeFilter;
      }
      const data = await getPropertyCompliance(propertyId, params);
      setComplianceTasks(data?.results || []);
      setComplianceSchedules(data?.schedules || []);
      setCommunalFireDoors(data?.fire_doors?.results || []);
      setCommunalFireDoorOptions(data?.fire_door_options || []);
      setCommunalFireDoorTotal(data?.fire_doors?.total || 0);
      if (data?.fire_doors?.page && data.fire_doors.page !== communalFireDoorPage) {
        setCommunalFireDoorPage(data.fire_doors.page);
      }
      setComplianceTotal(data?.total || 0);
      if (data?.page && data.page !== page) {
        setCompliancePage(data.page);
      }
    } catch (err) {
      console.error(err);
      setComplianceError("Failed to load compliance tasks.");
    } finally {
      setComplianceLoading(false);
    }
  }, [
    FIRE_DOOR_PAGE_SIZE,
    communalFireDoorPage,
    complianceAlertScope,
    compliancePage,
    complianceTypeFilter,
    propertyId,
  ]);

  const loadUnits = useCallback(async (page = 1) => {
    if (!propertyId) return;
    setUnitsLoading(true);
    try {
      const params = {
        page,
        page_size: UNITS_PAGE_SIZE,
        q: unitsSearch || undefined,
        missing_email: unitsMissingEmailOnly || undefined,
      };
      const data = await getUnitsByProperty(propertyId, params);
      const results = Array.isArray(data) ? data : data.results || [];
      const total = Array.isArray(data) ? results.length : data.total || 0;
      const maxPage = Math.max(1, Math.ceil(total / UNITS_PAGE_SIZE));

      if (page > maxPage) {
        const adjusted = await getUnitsByProperty(propertyId, {
          ...params,
          page: maxPage,
        });
        const adjustedResults = Array.isArray(adjusted)
          ? adjusted
          : adjusted.results || [];
        setUnits(adjustedResults);
        setUnitsTotal(total);
        setUnitsPage(maxPage);
        return;
      }

      setUnits(results);
      setUnitsTotal(total);
    } catch (err) {
      console.error(err);
      showToast("Failed to load units.", "error");
    } finally {
      setUnitsLoading(false);
    }
  }, [propertyId, showToast, unitsMissingEmailOnly, unitsSearch]);

  const loadUnitGroups = useCallback(async () => {
    if (!propertyId) return;
    setUnitGroupsLoading(true);
    try {
      const data = await getUnitGroups(propertyId);
      setUnitGroups(Array.isArray(data) ? data : data.results || []);
    } catch (err) {
      console.error(err);
      showToast("Failed to load unit groups.", "error");
    } finally {
      setUnitGroupsLoading(false);
    }
  }, [propertyId, showToast]);

  const loadGroupUnits = async (groupId, page = 1) => {
    setGroupUnitsLoadingById((prev) => ({ ...prev, [groupId]: true }));
    try {
      const data = await getUnitGroupUnits(propertyId, groupId, {
        page,
        page_size: GROUP_UNITS_PAGE_SIZE,
      });
      setGroupUnitsById((prev) => ({
        ...prev,
        [groupId]: data?.results || [],
      }));
      setGroupUnitsTotalById((prev) => ({
        ...prev,
        [groupId]: data?.total || 0,
      }));
      setGroupUnitsPageById((prev) => ({
        ...prev,
        [groupId]: data?.page || page,
      }));
    } catch (err) {
      console.error(err);
      showToast("Failed to load group units.", "error");
    } finally {
      setGroupUnitsLoadingById((prev) => ({ ...prev, [groupId]: false }));
    }
  };

  const loadData = useCallback(async () => {
    try {
      const [propertyData, typesData, complianceTypeData] = await Promise.all([
        getPropertyDetails(propertyId, { include_units: false }),
        getAssessmentTypes(),
        getComplianceTypes(),
      ]);

      setProperty(propertyData);
      setDirectors(propertyData?.directors || []);
      setNotesDraft(propertyData?.metadata?.notes || "");
      if (propertyData?.postcode) {
        setLocationPostcode(propertyData.postcode);
      }
      setAssessmentTypes(typesData || []);
      setComplianceTypes(complianceTypeData || []);
      setTicketsError("");

      // Set breadcrumb label to property name
      if (propertyData?.name) {
        setLabel(propertyId, propertyData.name);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load property details.");
    } finally {
      setLoading(false);
    }
  }, [propertyId, setLabel]);

  useEffect(() => {
    loadData();
    loadUnitGroups();
    setTicketsPage(1);

    // Cleanup: clear the label when leaving the page
    return () => {
      clearLabel(propertyId);
    };
  }, [clearLabel, loadData, loadUnitGroups, propertyId]);

  useEffect(() => {
    if (!propertyId) return;
    setUnitsPage(1);
  }, [propertyId, unitsMissingEmailOnly, unitsSearch]);

  useEffect(() => {
    if (!propertyId) return;
    loadUnits(unitsPage);
  }, [loadUnits, propertyId, unitsPage]);

  useEffect(() => {
    if (!unitsMissingEmailOnly) return;
    navigateToTab(0);
    const unitsSection = document.getElementById("units-section");
    if (unitsSection) {
      unitsSection.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [navigateToTab, unitsMissingEmailOnly]);

  useEffect(() => {
    if (requestedTab === "finance" && canAccessFinance) {
      navigate(`/properties/${propertyId}/finance?tab=overview`);
    }
  }, [canAccessFinance, requestedTab, propertyId, navigate]);

  useEffect(() => {
    const nextTab =
      requestedTab && Object.prototype.hasOwnProperty.call(TAB_INDEX_BY_QUERY, requestedTab)
        ? TAB_INDEX_BY_QUERY[requestedTab]
        : 0;
    setActiveTab(nextTab === 9 && !canAccessPurchaseOrders ? 0 : nextTab);
  }, [requestedTab, canAccessPurchaseOrders]);

  useEffect(() => {
    if (activeTab !== 6 || pendingComplianceScheduleScrollId == null) {
      return undefined;
    }
    let cancelled = false;
    const t = window.setTimeout(() => {
      if (cancelled) return;
      const el = document.getElementById(
        `compliance-schedule-row-${pendingComplianceScheduleScrollId}`
      );
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      setPendingComplianceScheduleScrollId(null);
    }, 50);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [activeTab, pendingComplianceScheduleScrollId]);

  useEffect(() => {
    if (requestedSummary !== "attention" || loading || !complianceAttentionItems.length) {
      return;
    }
    const summarySection = document.getElementById("property-attention-summary");
    if (summarySection) {
      summarySection.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [complianceAttentionItems.length, loading, requestedSummary]);

  useEffect(() => {
    if (propertyId) {
      loadTickets(ticketsPage);
    }
  }, [propertyId, loadTickets, ticketsPage]);

  useEffect(() => {
    setTicketsPage(1);
  }, [propertyId, showResolvedClosed]);

  useEffect(() => {
    setCompliancePage(1);
  }, [propertyId, complianceTypeFilter]);

  useEffect(() => {
    setCommunalFireDoorPage(1);
  }, [propertyId]);

  useEffect(() => {
    loadCompliance(compliancePage);
  }, [communalFireDoorPage, compliancePage, loadCompliance]);

  const handleExportUnits = async () => {
    if (isDisabledPropertyAction()) return;
    try {
      await downloadUnitsExcel(propertyId);
    } catch (err) {
      console.error(err);
      showToast("Failed to export units.", "error");
    }
  };

  const handleSaveNotes = async () => {
    if (isDisabledPropertyAction()) return;
    if (!propertyId) return;
    setNotesSaving(true);
    setNotesError("");
    try {
      const payload = { notes: notesDraft || "", row_version: property?.row_version };
      const response = await updatePropertyMetadata(propertyId, payload);
      const nextMetadata =
        response?.data?.metadata ||
        { ...(property?.metadata || {}), notes: notesDraft || "" };
      setProperty((prev) => ({
        ...prev,
        metadata: nextMetadata,
        row_version: response?.data?.row_version ?? prev?.row_version,
      }));
      showToast("Notes saved.");
    } catch (err) {
      console.error(err);
      const message = getApiErrorMessage(err, "Failed to save notes.");
      setNotesError(message);
      showToast(message, "error");
    } finally {
      setNotesSaving(false);
    }
  };

  const refreshDirectors = async () => {
    const data = await getPropertyDirectors(propertyId);
    const next = Array.isArray(data) ? data : [];
    setDirectors(next);
    setProperty((prev) => (prev ? { ...prev, directors: next } : prev));
  };

  const handleOpenDirectorCreate = () => {
    if (isDisabledPropertyAction()) return;
    setEditingDirector(null);
    setDirectorForm({ name: "", email: "", phone: "" });
    setDirectorDialogOpen(true);
  };

  const handleOpenDirectorEdit = (director) => {
    if (isDisabledPropertyAction()) return;
    setEditingDirector(director);
    setDirectorForm({
      name: director?.name || "",
      email: director?.email || "",
      phone: director?.phone || "",
    });
    setDirectorDialogOpen(true);
  };

  const handleSaveDirector = async () => {
    if (isDisabledPropertyAction()) return;
    if (!propertyId) return;
    const payload = {
      name: (directorForm.name || "").trim(),
      email: (directorForm.email || "").trim(),
      phone: (directorForm.phone || "").trim(),
    };
    if (!payload.name) {
      showToast("Director name is required.", "error");
      return;
    }

    setDirectorSubmitting(true);
    try {
      if (editingDirector?.id) {
        await updatePropertyDirector(propertyId, editingDirector.id, payload);
        showToast("Director updated");
      } else {
        await createPropertyDirector(propertyId, payload);
        showToast("Director added");
      }
      await refreshDirectors();
      setDirectorDialogOpen(false);
    } catch (err) {
      console.error(err);
      const message =
        err?.response?.data?.error || "Failed to save director.";
      showToast(message, "error");
    } finally {
      setDirectorSubmitting(false);
    }
  };

  const handleDeleteDirector = async (directorId) => {
    if (isDisabledPropertyAction()) return;
    if (!propertyId) return;
    const confirmed = window.confirm("Delete this director?");
    if (!confirmed) return;
    try {
      await deletePropertyDirector(propertyId, directorId);
      await refreshDirectors();
      showToast("Director deleted");
    } catch (err) {
      console.error(err);
      const message =
        err?.response?.data?.error || "Failed to delete director.";
      showToast(message, "error");
    }
  };

  const handleEmailAllDirectors = async () => {
    if (isDisabledPropertyAction()) return;
    try {
      const data = await getDirectorsEmailLink(propertyId);
      if (!data?.mailto) {
        showToast("No directors with email addresses found.", "warning");
        return;
      }
      window.location.href = normalizeMailtoUrl(data.mailto);
    } catch (err) {
      console.error(err);
      const message =
        err?.response?.data?.error || "Failed to prepare email link.";
      showToast(message, "error");
    }
  };

  const handleEmailDirector = (director) => {
    if (isDisabledPropertyAction()) return;
    const email = formatMailto(director?.email || "");
    if (!email) {
      showToast("Director has no email address.", "warning");
      return;
    }
    const subject = encodeURIComponent(property?.name || "Property");
    window.location.href = normalizeMailtoUrl(
      `mailto:${email}?subject=${subject}`
    );
  };

  const handleOpenComplianceCreate = () => {
    if (isDisabledPropertyAction()) return;
    const today = new Date();
    const todayStr = today.toISOString().split("T")[0];
    setComplianceCreateForm({
      schedule_id: "",
      date_inspected: todayStr,
      result: "",
      completed_by_name: "",
      fail_notes: "",
      document: null,
      fire_door_checklist: buildDefaultFireDoorChecklist(),
    });
    setComplianceCreateChecklistOpen(false);
    setComplianceCreateChecklistDialogOpen(false);
    setComplianceCreateOpen(true);
  };

  const handleOpenComplianceCreateChecklist = () => {
    if (isDisabledPropertyAction()) return;
    if (!communalFireDoorOptions.length) return;
    setComplianceCreateChecklistOpen(true);
    setComplianceCreateChecklistDialogOpen(true);
  };

  const handleRemoveComplianceCreateChecklist = () => {
    setComplianceCreateChecklistOpen(false);
    setComplianceCreateChecklistDialogOpen(false);
  };

  const handleOpenCommunalFireDoorCreate = () => {
    if (isDisabledPropertyAction()) return;
    setCommunalFireDoorForm({
      building_name: "",
      floor: "",
      door_type: "communal",
      identification: "",
    });
    setCommunalFireDoorOpen(true);
  };

  const handleOpenComplianceScheduleCreate = () => {
    if (isDisabledPropertyAction()) return;
    setComplianceScheduleForm({
      compliance_type_id: "",
      frequency: "",
    });
    setComplianceScheduleOpen(true);
  };

  const handleComplianceScheduleCreate = async () => {
    if (isDisabledPropertyAction()) return;
    if (!propertyId) return;
    if (!complianceScheduleForm.compliance_type_id || !complianceScheduleForm.frequency) {
      showToast("Type and frequency are required.", "error");
      return;
    }

    setComplianceScheduleSubmitting(true);
    try {
      await createComplianceSchedule({
        property_id: propertyId,
        compliance_type_id: complianceScheduleForm.compliance_type_id,
        frequency: complianceScheduleForm.frequency,
      });
      showToast("Compliance schedule saved");
      setComplianceScheduleOpen(false);
      await loadCompliance(1);
    } catch (err) {
      console.error(err);
      const message =
        err?.response?.data?.error || "Failed to create compliance schedule.";
      showToast(message, "error");
    } finally {
      setComplianceScheduleSubmitting(false);
    }
  };

  const handleCommunalFireDoorCreate = async () => {
    if (isDisabledPropertyAction()) return;
    if (!propertyId) return;
    if (
      !communalFireDoorForm.building_name.trim() ||
      communalFireDoorForm.floor === "" ||
      !communalFireDoorForm.identification.trim()
    ) {
      showToast("Building, floor, type, and identification are required.", "error");
      return;
    }
    if (!/^\d+$/.test(String(communalFireDoorForm.floor))) {
      showToast("Floor must be numeric.", "error");
      return;
    }

    setCommunalFireDoorSubmitting(true);
    try {
      await createCommunalFireDoor(propertyId, {
        building_name: communalFireDoorForm.building_name.trim(),
        floor: Number(communalFireDoorForm.floor),
        door_type: communalFireDoorForm.door_type,
        identification: communalFireDoorForm.identification.trim(),
      });
      showToast("Communal fire door saved");
      setCommunalFireDoorOpen(false);
      await loadCompliance(compliancePage);
    } catch (err) {
      console.error(err);
      const message =
        err?.response?.data?.error || "Failed to create communal fire door.";
      showToast(message, "error");
    } finally {
      setCommunalFireDoorSubmitting(false);
    }
  };

  const handleComplianceCreate = async () => {
    if (isDisabledPropertyAction()) return;
    if (!propertyId) return;
    if (
      !complianceCreateForm.schedule_id ||
      !complianceCreateForm.date_inspected ||
      !complianceCreateForm.result ||
      !complianceCreateForm.completed_by_name.trim()
    ) {
      showToast("Schedule, inspected date, result, and completed by are required.", "error");
      return;
    }
    setComplianceCreateSubmitting(true);
    try {
      const payload = new FormData();
      payload.append("property_id", propertyId);
      payload.append("schedule_id", complianceCreateForm.schedule_id);
      payload.append("date_inspected", complianceCreateForm.date_inspected);
      payload.append("result", complianceCreateForm.result);
      payload.append("completed_by_name", complianceCreateForm.completed_by_name.trim());
      if (complianceCreateForm.result === "Fail") {
        payload.append("fail_notes", complianceCreateForm.fail_notes || "");
      }
      if (
        createChecklistEnabled &&
        complianceCreateChecklistOpen &&
        complianceCreateForm.fire_door_checklist.length
      ) {
        payload.append(
          "fire_door_checklist",
          JSON.stringify(complianceCreateForm.fire_door_checklist)
        );
      }
      if (complianceCreateForm.document) {
        payload.append("document", complianceCreateForm.document);
      }
      await createComplianceTask(payload);
      showToast("Compliance task created");
      setComplianceCreateOpen(false);
      loadCompliance(1);
    } catch (err) {
      console.error(err);
      const message =
        err?.response?.data?.error || "Failed to create compliance task.";
      showToast(message, "error");
    } finally {
      setComplianceCreateSubmitting(false);
    }
  };

  const handleOpenComplianceEdit = (task) => {
    if (isDisabledPropertyAction()) return;
    setComplianceSelectedTask(task);
    setComplianceEditForm({
      compliance_type_id: String(task.compliance_type_id || ""),
      frequency: task.frequency || "",
      due_date: task.due_date || "",
      date_inspected: task.date_inspected || "",
      result: task.result || "",
      fail_notes: task.fail_notes || "",
      document: null,
      fire_door_checklist: normalizeFireDoorChecklist(task.fire_door_checklist || []),
    });
    setComplianceEditChecklistOpen(Boolean(task.fire_door_checklist?.length));
    setComplianceEditChecklistDialogOpen(false);
    setComplianceEditOpen(true);
  };

  const handleOpenComplianceEditChecklist = () => {
    if (isDisabledPropertyAction()) return;
    if (!communalFireDoorOptions.length) return;
    setComplianceEditChecklistOpen(true);
    setComplianceEditChecklistDialogOpen(true);
  };

  const handleRemoveComplianceEditChecklist = () => {
    setComplianceEditChecklistOpen(false);
    setComplianceEditChecklistDialogOpen(false);
  };

  const handleOpenComplianceViewChecklist = (task) => {
    setComplianceViewChecklistTask(task);
    setComplianceViewChecklistRows(normalizeFireDoorChecklist(task.fire_door_checklist || []));
    setComplianceViewChecklistDialogOpen(true);
  };

  const handleComplianceEdit = async () => {
    if (isDisabledPropertyAction()) return;
    if (!complianceSelectedTask) return;
    if (
      !complianceEditForm.compliance_type_id ||
      !complianceEditForm.frequency ||
      !complianceEditForm.due_date ||
      !complianceEditForm.result
    ) {
      showToast("Type, frequency, due date, and result are required.", "error");
      return;
    }
    setComplianceEditSubmitting(true);
    try {
      const payload = new FormData();
      payload.append("compliance_type_id", complianceEditForm.compliance_type_id);
      payload.append("frequency", complianceEditForm.frequency);
      payload.append("due_date", complianceEditForm.due_date);
      payload.append("date_inspected", complianceEditForm.date_inspected || "");
      payload.append("result", complianceEditForm.result);
      payload.append("fail_notes", complianceEditForm.fail_notes || "");
      if (editChecklistEnabled && complianceEditChecklistOpen) {
        payload.append(
          "fire_door_checklist",
          JSON.stringify(complianceEditForm.fire_door_checklist)
        );
      }
      payload.append("row_version", String(complianceSelectedTask.row_version || 1));
      if (complianceEditForm.document) {
        payload.append("document", complianceEditForm.document);
      }
      await updateComplianceTask(complianceSelectedTask.id, payload);
      showToast("Compliance task updated");
      setComplianceEditOpen(false);
      setComplianceSelectedTask(null);
      loadCompliance(compliancePage);
    } catch (err) {
      console.error(err);
      const message = getApiErrorMessage(err, "Failed to update compliance task.");
      showToast(message, "error");
    } finally {
      setComplianceEditSubmitting(false);
    }
  };

  const handleDeleteComplianceTask = async (task) => {
    if (isDisabledPropertyAction()) return;
    const confirmed = window.confirm("Delete this compliance task?");
    if (!confirmed) return;
    try {
      await deleteComplianceTask(task.id);
      showToast("Compliance task deleted");
      loadCompliance(1);
    } catch (err) {
      console.error(err);
      const message =
        err?.response?.data?.error || "Failed to delete compliance task.";
      showToast(message, "error");
    }
  };

  const handleDeleteUnit = async (unitId) => {
    if (isDisabledPropertyAction()) return;
    const confirmed = window.confirm("Delete this unit?");
    if (!confirmed) return;
    try {
      await deleteUnit(unitId);
      showToast("Unit deleted");
      await loadData();
      await loadUnits(unitsPage);
    } catch (err) {
      console.error(err);
      showToast("Failed to delete unit.", "error");
    }
  };

  const handleOpenTicketCreate = () => {
    if (isDisabledPropertyAction()) return;
    setTicketCreateError("");
    setTicketCreatePropertyEnquiryForm(null);
    setTicketCreatePropertyEnquiryOptions([]);
    setTicketCreateForm({
      title: "",
      description: "",
      priority: "Medium",
      type: "",
      requester_name: user?.name || user?.claims?.name || "",
    });
    setTicketCreateOpen(true);
  };

  const handleCreateTicket = async () => {
    if (isDisabledPropertyAction()) return;
    if (!propertyId) return;
    const payload = {
      title: (ticketCreateForm.title || "").trim(),
      description: (ticketCreateForm.description || "").trim(),
      priority: ticketCreateForm.priority,
      type: ticketCreateForm.type,
      requester_name: (ticketCreateForm.requester_name || "").trim(),
      status: "Open",
      property_id: Number(propertyId),
    };

    if (!payload.title || !payload.description || !payload.requester_name || !payload.type) {
      setTicketCreateError("Title, description, requester name, and type are required.");
      return;
    }

    if (payload.type === "Management Pack" && !ticketCreatePropertyEnquiryForm) {
      setTicketCreateError("Property enquiry form is required for Management Pack tickets.");
      return;
    }

    if (payload.type === "Management Pack") {
      payload.property_enquiry_form_type = ticketCreatePropertyEnquiryForm.form_type;
      payload.property_enquiry_form_id = ticketCreatePropertyEnquiryForm.id;
    }

    setTicketCreateSubmitting(true);
    setTicketCreateError("");
    try {
      await createTicket(payload);
      setTicketCreateOpen(false);
      showToast("Ticket created");
      setTicketsPage(1);
      await loadTickets(1);
    } catch (err) {
      console.error(err);
      const message =
        err?.response?.data?.error || "Failed to create ticket.";
      setTicketCreateError(message);
    } finally {
      setTicketCreateSubmitting(false);
    }
  };

  const formatDate = formatDateUk;

  useEffect(() => {
    let ignore = false;

    const loadTicketCreatePropertyEnquiries = async () => {
      if (!ticketCreateOpen || ticketCreateForm.type !== "Management Pack" || !propertyId) {
        setTicketCreatePropertyEnquiryForm(null);
        setTicketCreatePropertyEnquiryOptions([]);
        setTicketCreatePropertyEnquiryLoading(false);
        return;
      }

      setTicketCreatePropertyEnquiryLoading(true);
      try {
        const forms = [];
        let pageNumber = 1;
        let totalPages = 1;

        while (pageNumber <= totalPages) {
          const data = await getPropertyLPE1Forms(propertyId, {
            page: pageNumber,
            page_size: 100,
          });
          forms.push(...(data?.results || []));
          totalPages = data?.total_pages || 1;
          pageNumber += 1;
        }

        if (ignore) return;

        setTicketCreatePropertyEnquiryOptions(
          (forms || []).map((form) => {
            const formType = form?.form_type || "LPE1";
            const unitLabel = form?.unit_name || `Unit ${form?.unit_id ?? "-"}`;
            return {
              id: form.id,
              form_type: formType,
              unit_id: form.unit_id,
              unit_name: unitLabel,
              label: `${formType} · ${unitLabel} · #${form.id}`,
            };
          })
        );
      } catch (err) {
        console.error("Failed to load property enquiry forms for ticket create:", err);
        if (!ignore) {
          setTicketCreatePropertyEnquiryOptions([]);
          setTicketCreatePropertyEnquiryForm(null);
        }
      } finally {
        if (!ignore) {
          setTicketCreatePropertyEnquiryLoading(false);
        }
      }
    };

    loadTicketCreatePropertyEnquiries();

    return () => {
      ignore = true;
    };
  }, [ticketCreateOpen, ticketCreateForm.type, propertyId]);

  const getAssessmentTypeName = (id) => {
    const match = assessmentTypes.find((t) => t.id === id);
    return match ? match.name : "Unknown Type";
  };

  const handleConfirmImport = async () => {
    if (isDisabledPropertyAction()) return;
    if (!importFile) return;
    setImportSubmitting(true);
    try {
      const result = await importUnits(propertyId, importFile);
      await loadData();
      await loadUnits(unitsPage);
      if (result?.warnings?.length) {
        showToast(
          "Some units already exist. Missing fields were filled where provided.",
          "warning"
        );
      } else {
        showToast("Units imported");
      }
      setImportPreviewOpen(false);
      setImportRows([]);
      setImportFile(null);
    } catch (err) {
      console.error(err);
      showToast("Failed to import units.", "error");
    } finally {
      setImportSubmitting(false);
    }
  };

  const handleImportAnyway = () => {
    if (isDisabledPropertyAction()) return;
    const totalCount = importRows.length;
    const skipCount = importErrorCount;
    const importCount = Math.max(totalCount - skipCount, 0);
    const confirmed = window.confirm(
      `${importCount} of ${totalCount} units will be imported, ${skipCount} will be skipped. Is that ok?`
    );
    if (!confirmed) return;
    handleConfirmImport();
  };

  const handleOpenGroupCreate = () => {
    if (isDisabledPropertyAction()) return;
    setGroupCreateName("");
    setGroupCreateOpen(true);
  };

  const handleCreateGroup = async () => {
    if (isDisabledPropertyAction()) return;
    const name = groupCreateName.trim();
    if (!name) return;
    setGroupCreateSubmitting(true);
    try {
      await createUnitGroup(propertyId, name);
      showToast("Group created");
      setGroupCreateOpen(false);
      setGroupCreateName("");
      await loadUnitGroups();
    } catch (err) {
      console.error(err);
      const message = err?.response?.data?.error || "Failed to create group.";
      showToast(message, "error");
    } finally {
      setGroupCreateSubmitting(false);
    }
  };

  const handleToggleGroupExpand = (groupId) => {
    if (expandedGroupId === groupId) {
      setExpandedGroupId(null);
      return;
    }
    setExpandedGroupId(groupId);
    const currentPage = groupUnitsPageById[groupId] || 1;
    loadGroupUnits(groupId, currentPage);
  };

  const handleOpenGroupPicker = (group) => {
    if (isDisabledPropertyAction()) return;
    setGroupPickerGroup(group);
    setGroupPickerSearch("");
    setGroupPickerPage(1);
    setGroupPickerSelected([]);
    setGroupPickerOpen(true);
  };

  const handleCloseGroupPicker = () => {
    setGroupPickerOpen(false);
    setGroupPickerGroup(null);
    setGroupPickerUnits([]);
    setGroupPickerSelected([]);
    setGroupPickerTotal(0);
  };

  const handleTogglePickerUnit = (unitId) => {
    setGroupPickerSelected((prev) =>
      prev.includes(unitId)
        ? prev.filter((id) => id !== unitId)
        : [...prev, unitId]
    );
  };

  const loadGroupPickerUnits = useCallback(async (groupId, page, search) => {
    setGroupPickerLoading(true);
    try {
      const data = await getUnitGroupAvailableUnits(propertyId, groupId, {
        page,
        page_size: GROUP_PICKER_PAGE_SIZE,
        q: search,
      });
      setGroupPickerUnits(data?.results || []);
      setGroupPickerTotal(data?.total || 0);
    } catch (err) {
      console.error(err);
      showToast("Failed to load available units.", "error");
    } finally {
      setGroupPickerLoading(false);
    }
  }, [propertyId, showToast]);

  useEffect(() => {
    if (!groupPickerOpen || !groupPickerGroup?.id) return;
    const timeout = setTimeout(() => {
      loadGroupPickerUnits(
        groupPickerGroup.id,
        groupPickerPage,
        groupPickerSearch
      );
    }, 300);
    return () => clearTimeout(timeout);
  }, [groupPickerOpen, groupPickerGroup?.id, groupPickerPage, groupPickerSearch, loadGroupPickerUnits]);

  const handleAddUnitsToGroup = async () => {
    if (isDisabledPropertyAction()) return;
    if (!groupPickerGroup || !groupPickerSelected.length) return;
    try {
      await addUnitsToGroup(propertyId, groupPickerGroup.id, groupPickerSelected);
      showToast("Units added to group");
      await loadUnitGroups();
      if (expandedGroupId === groupPickerGroup.id) {
        const page = groupUnitsPageById[groupPickerGroup.id] || 1;
        await loadGroupUnits(groupPickerGroup.id, page);
      }
      setGroupPickerSelected([]);
      await loadGroupPickerUnits(
        groupPickerGroup.id,
        groupPickerPage,
        groupPickerSearch
      );
    } catch (err) {
      console.error(err);
      const message = err?.response?.data?.error || "Failed to add units.";
      showToast(message, "error");
    }
  };

  const handleRemoveUnitFromGroup = async (groupId, unitId) => {
    if (isDisabledPropertyAction()) return;
    try {
      await removeUnitsFromGroup(propertyId, groupId, [unitId]);
      showToast("Unit removed from group");
      await loadUnitGroups();
      const page = groupUnitsPageById[groupId] || 1;
      await loadGroupUnits(groupId, page);
    } catch (err) {
      console.error(err);
      const message = err?.response?.data?.error || "Failed to remove unit.";
      showToast(message, "error");
    }
  };

  const handleDeleteUnitGroup = async (group) => {
    if (isDisabledPropertyAction()) return;
    const confirmed = window.confirm(
      `Delete unit group "${group.name}"? This cannot be undone.`
    );
    if (!confirmed) return;
    try {
      await deleteUnitGroup(propertyId, group.id);
      showToast("Unit group deleted");
      if (expandedGroupId === group.id) {
        setExpandedGroupId(null);
      }
      await loadUnitGroups();
    } catch (err) {
      console.error(err);
      const message = err?.response?.data?.error || "Failed to delete unit group.";
      showToast(message, "error");
    }
  };

  const handleOpenLocationDialog = () => {
    if (isDisabledPropertyAction()) return;
    setLocationError("");
    setLocationResults([]);
    setLocationSelected("");
    if (property?.postcode) {
      setLocationPostcode(property.postcode);
    }
    setLocationDialogOpen(true);
  };

  const handleLocationLookup = async () => {
    if (isDisabledPropertyAction()) return;
    setLocationError("");
    setLocationResults([]);
    setLocationSelected("");
    const trimmed = locationPostcode.trim();
    if (!trimmed) {
      setLocationError("Enter a postcode to search.");
      return;
    }
    setLocationLoading(true);
    try {
      const data = await lookupAddress(trimmed, locationFilter.trim());
      const suggestions = data?.suggestions || [];
      setLocationResults(suggestions);
      if (!suggestions.length) {
        setLocationError("No addresses found for that postcode.");
      }
    } catch (err) {
      console.error("Address lookup error:", err);
      setLocationError(err?.message || "Address lookup failed.");
    } finally {
      setLocationLoading(false);
    }
  };

  const handleLocationSelect = async (value) => {
    if (isDisabledPropertyAction()) return;
    setLocationSelected(value);
    setLocationError("");
    const selected = locationResults[Number(value)] || null;
    if (!selected?.id) return;
    try {
      const address = await getAddressById(selected.id);
      const payload = {
        address_line1: address.line_1 || property.address_line1 || "",
        address_line2: address.line_2 || property.address_line2 || "",
        city: address.town_or_city || address.locality || property.city || "",
        county: address.county || property.county || "",
        postcode: address.postcode || locationPostcode.trim() || property.postcode || "",
        ideal_postcodes_id: address.ideal_postcodes_id || selected.id,
        uprn: address.uprn || null,
        address_latitude: address.latitude ?? null,
        address_longitude: address.longitude ?? null,
      };
      const result = await updateProperty(propertyId, { ...payload, row_version: property?.row_version });
      setProperty((prev) => ({
        ...prev,
        ...payload,
        row_version: result?.row_version ?? prev?.row_version,
      }));
      if (payload.postcode) {
        setLocationPostcode(payload.postcode);
      }
      showToast("Location updated");
      setLocationDialogOpen(false);
    } catch (err) {
      console.error("Address get error:", err);
      setLocationError("Failed to update location.");
    }
  };

  const handleCancelImport = () => {
    if (importSubmitting) return;
    setImportPreviewOpen(false);
    setImportRows([]);
    setImportFile(null);
    setImportErrorCount(0);
    setImportPreviewError("");
  };

  const handleUnitsPageChange = (event, page) => {
    setUnitsPage(page);
  };

  const handleOpenUnitsSearch = () => {
    setUnitsSearchDraft(unitsSearch);
    setUnitsSearchOpen(true);
  };

  const handleApplyUnitsSearch = () => {
    setUnitsSearch(unitsSearchDraft.trim());
    setUnitsPage(1);
    setUnitsSearchOpen(false);
  };

  const handleClearUnitsSearch = () => {
    setUnitsSearch("");
    setUnitsSearchDraft("");
    setUnitsPage(1);
  };

  const handleClearMissingEmailFilter = () => {
    const nextParams = new URLSearchParams(location.search);
    nextParams.delete("units_filter");
    navigate(
      {
        pathname: location.pathname,
        search: nextParams.toString() ? `?${nextParams.toString()}` : "",
      },
      { replace: true }
    );
  };

  const handleEmailAllUnits = async () => {
    if (isDisabledPropertyAction()) return;
    try {
      const data = await getUnitsEmailLink(propertyId);
      if (!data?.mailto) {
        showToast("No units with email addresses found.", "warning");
        return;
      }
      window.location.href = normalizeMailtoUrl(data.mailto);
    } catch (err) {
      console.error(err);
      const message =
        err?.response?.data?.error || "Failed to prepare email link.";
      showToast(message, "error");
    }
  };

  const handleEmailGroupUnits = async (group) => {
    if (isDisabledPropertyAction()) return;
    try {
      const data = await getGroupUnitsEmailLink(propertyId, group.id);
      if (!data?.mailto) {
        showToast("No units with email addresses found.", "warning");
        return;
      }
      window.location.href = normalizeMailtoUrl(data.mailto);
    } catch (err) {
      console.error(err);
      const message =
        err?.response?.data?.error || "Failed to prepare email link.";
      showToast(message, "error");
    }
  };

  const applyImportUniqErrors = (rows, existingUnits) => {
    const normalizeImportName = (value) =>
      value ? String(value).trim().toLowerCase() : "";

    const existingCodes = new Set(
      (existingUnits || [])
        .map((unit) => unit.unit_code)
        .filter((value) => value)
    );
    const seenNames = new Set();
    const seenCodes = new Set();

    return rows.map((row) => {
      const errors = [];
      if (row.error) {
        errors.push(row.error);
      }

      if (row.unit_name) {
        const normalizedName = normalizeImportName(row.unit_name);
        if (seenNames.has(normalizedName)) {
          errors.push("Duplicate unit name in file");
        }
        seenNames.add(normalizedName);
      }

      if (row.unit_code) {
        if (existingCodes.has(row.unit_code)) {
          errors.push("Duplicate unit code");
        } else if (seenCodes.has(row.unit_code)) {
          errors.push("Duplicate unit code in file");
        }
        seenCodes.add(row.unit_code);
      }

      return {
        ...row,
        error: errors.length ? errors.join("; ") : row.error,
      };
    });
  };

  if (loading) {
    return (
      <Layout>
        <Box sx={{ p: 3 }}>
          <Skeleton variant="text" width={300} height={40} />
          <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
        </Box>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <Box sx={{ p: 3 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </Layout>
    );
  }

  if (!property) {
    return (
      <Layout>
        <Box sx={{ p: 3 }}>
          <Alert severity="warning">No property details found.</Alert>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ maxWidth: "1400px", mx: "auto", p: 3 }}>
        {/* HEADER */}
        <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight={700}>
              {property.name}
            </Typography>
          </Box>
        </Grid>

        {complianceAttentionItems.length > 0 && (
          <Paper
            id="property-attention-summary"
            elevation={2}
            sx={{
              p: 3,
              borderRadius: 2,
              mb: 3,
              border: "1px solid",
              borderColor: "error.light",
              backgroundColor: "rgba(211, 47, 47, 0.04)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <ErrorIcon color="error" />
              <Typography variant="h6" fontWeight={600}>
                Compliance Summary
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              The following items need attention.
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {complianceAttentionItems.map((item) => (
                <Box component="li" key={item.key} sx={{ mb: 0.5 }}>
                  {item.target?.type === "route" ? (
                    <Link
                      component={RouterLink}
                      to={item.target.href}
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: "error.dark",
                        textUnderlineOffset: 3,
                        "&:hover": { color: "error.main" },
                      }}
                    >
                      {item.label}
                    </Link>
                  ) : item.target?.type === "complianceSchedule" ? (
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={() => {
                        navigateToTab(6);
                        setPendingComplianceScheduleScrollId(item.target.scheduleId);
                      }}
                      sx={{
                        fontWeight: 600,
                        cursor: "pointer",
                        border: "none",
                        background: "none",
                        padding: 0,
                        font: "inherit",
                        textAlign: "left",
                        color: "error.dark",
                        textDecoration: "underline",
                        textUnderlineOffset: 3,
                        "&:hover": { color: "error.main" },
                      }}
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <Typography variant="body2">{item.label}</Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Paper>
        )}

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(event, value) => navigateToTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ flex: 1, minWidth: 0 }}
          >
            <Tab label="Details" />
            <Tab label="Tickets" />
            <Tab label="Insurance" />
            <Tab label="Utilities" />
            <Tab label="Assessments" />
            <Tab label="Property Enquiries" />
            <Tab label="Compliance" />
            <Tab label="Notes" />
            {canAccessFinance && <Tab label="Finance" value={8} />}
            {canAccessPurchaseOrders && <Tab label="Purchase Orders" value={9} />}
          </Tabs>

          {canEdit && (
            <Button
              variant="contained"
              onClick={() => navigate(`/properties/${propertyId}/edit`)}
              sx={{ ml: 2, flexShrink: 0 }}
            >
              Edit Property
            </Button>
          )}
        </Box>

        {canAccessPurchaseOrders && <TabPanel value={activeTab} index={9}>
          <PurchaseOrdersPanel propertyId={propertyId} onMessage={showToast} />
        </TabPanel>}

        <TabPanel value={activeTab} index={0}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 4 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Property Details
            </Typography>

            <Grid container spacing={{ xs: 2, md: 4 }}>
              <Grid item xs={12} sm={6}>
                <Typography><strong>Type:</strong> {property.property_type}</Typography>
                <Typography><strong>Estate Code:</strong> {property.estate_code || "N/A"}</Typography>
                <Typography><strong>Property Manager:</strong> {property.property_manager || "N/A"}</Typography>
                <Typography><strong>Freeholder Name:</strong> {property.freeholder_name || "N/A"}</Typography>
                <Typography sx={{ whiteSpace: "pre-line" }}><strong>Freeholder Address:</strong> {property.freeholder_address || "N/A"}</Typography>
                <Typography><strong>Management Structure:</strong> {property.rtm_rmc_type === "RMC" ? "Residents' Management Company (RMC)" : property.rtm_rmc_type === "RTM" ? "Right to Manage Company (RTM)" : "N/A"}</Typography>
                <Typography><strong>{property.rtm_rmc_type || "Management Structure"}:</strong> {property.rtm_rmc_name || "N/A"}</Typography>
                <Typography><strong>Company Number:</strong> {property.company_number || "N/A"}</Typography>
                <Typography><strong>UTR Number:</strong> {property.utr_number || "N/A"}</Typography>
                <Typography><strong>Created:</strong> {formatDate(property.created_at_utc)}</Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography>
                  <strong>Address:</strong>{" "}
                  {property.address_line1}
                  {property.address_line2 ? `, ${property.address_line2}` : ""},{" "}
                  {property.city}
                </Typography>
                <Typography><strong>Postcode:</strong> {property.postcode}</Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography><strong>Financial Year Start:</strong> {financialYearStartLabel}</Typography>
                <Typography><strong>Bank Name:</strong> {property.bank_name || "N/A"}</Typography>
                <Typography><strong>Account Number:</strong> {property.account_number || "N/A"}</Typography>
                <Typography><strong>Sort Code:</strong> {property.sort_code || "N/A"}</Typography>
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 4 }}>
            <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>
                Directors
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  onClick={handleEmailAllDirectors}
                  disabled={propertyActionsDisabled || !directors.length}
                >
                  Email All
                </Button>
                {canEdit && (
                  <Button
                    variant="outlined"
                    onClick={handleOpenDirectorCreate}
                    disabled={propertyActionsDisabled}
                  >
                    Add Director
                  </Button>
                )}
              </Stack>
            </Grid>

            {directors.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Phone</TableCell>
                      {(canEdit || canDelete) && <TableCell align="right">Actions</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {directors.map((director) => (
                      <TableRow
                        key={director.id}
                        hover={canEdit && !propertyActionsDisabled}
                        onClick={
                          canEdit && !propertyActionsDisabled
                            ? () => handleOpenDirectorEdit(director)
                            : undefined
                        }
                        sx={canEdit && !propertyActionsDisabled ? { cursor: "pointer" } : undefined}
                      >
                        <TableCell>{director.name}</TableCell>
                        <TableCell>{director.email || "-"}</TableCell>
                        <TableCell>{director.phone || "-"}</TableCell>
                        {(canEdit || canDelete) && (
                          <TableCell align="right">
                            {canDelete && (
                              <Button
                                size="small"
                                color="error"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleDeleteDirector(director.id);
                                }}
                                disabled={propertyActionsDisabled}
                              >
                                Remove
                              </Button>
                            )}
                            <Tooltip title="Email director">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleEmailDirector(director);
                                  }}
                                  disabled={propertyActionsDisabled || !director.email}
                                  sx={{ ml: 1 }}
                                >
                                  <EmailOutlinedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography>No directors added.</Typography>
            )}
          </Paper>

          {property.address_latitude !== null &&
            property.address_latitude !== undefined &&
            property.address_longitude !== null &&
            property.address_longitude !== undefined ? (
            <Paper elevation={2} sx={{ p: 2, borderRadius: 2, mb: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <LocationOnIcon fontSize="small" color="action" />
                <Typography variant="subtitle1" fontWeight={600}>
                  Location
                </Typography>
              </Box>
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  height: { xs: 220, sm: 280 },
                  borderRadius: 2,
                  overflow: "hidden",
                }}
              >
                <iframe
                  title="Property location"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  src={`https://www.google.com/maps?q=${property.address_latitude},${property.address_longitude}&z=16&output=embed`}
                />
              </Box>
            </Paper>
          ) : (
            <Paper elevation={2} sx={{ p: 2, borderRadius: 2, mb: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <LocationOnIcon fontSize="small" color="action" />
                <Typography variant="subtitle1" fontWeight={600}>
                  Location
                </Typography>
              </Box>
              <Button
                variant="outlined"
                onClick={handleOpenLocationDialog}
                disabled={propertyActionsDisabled}
                sx={{ mt: 1 }}
              >
                Find location for this property
              </Button>
            </Paper>
          )}

          {/* UNIT GROUPS */}
          {unitGroupsLoading ? (
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 4 }}>
              <Skeleton height={40} width={240} />
              <Skeleton height={120} sx={{ mt: 2 }} />
            </Paper>
          ) : unitGroups.length ? (
            <SectionUnitGroups
              groups={unitGroups}
              canCreate={canCreate}
              canDelete={canDelete}
              actionsDisabled={propertyActionsDisabled}
              expandedGroupId={expandedGroupId}
              groupUnitsById={groupUnitsById}
              groupUnitsTotalById={groupUnitsTotalById}
              groupUnitsPageById={groupUnitsPageById}
              groupUnitsLoadingById={groupUnitsLoadingById}
              groupUnitsPageSize={GROUP_UNITS_PAGE_SIZE}
              onToggleExpand={handleToggleGroupExpand}
              onGroupPageChange={(groupId, page) => loadGroupUnits(groupId, page)}
              onAddUnits={handleOpenGroupPicker}
              onEmailGroup={handleEmailGroupUnits}
              onRemoveUnit={handleRemoveUnitFromGroup}
              onNavigateUnit={(unitId) => navigate(`/units/${unitId}/details`)}
              onCreateGroup={handleOpenGroupCreate}
              onDeleteGroup={handleDeleteUnitGroup}
            />
          ) : canCreate ? (
            <Box sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                onClick={handleOpenGroupCreate}
                disabled={propertyActionsDisabled}
              >
                Group Units
              </Button>
            </Box>
          ) : null}

          {/* UNITS */}
          <SectionUnits
            units={units}
            unitsPage={unitsPage}
            unitsTotal={unitsTotal}
            unitsPageSize={UNITS_PAGE_SIZE}
            unitsLoading={unitsLoading}
            onUnitsPageChange={handleUnitsPageChange}
            onEmailAllUnits={handleEmailAllUnits}
            canCreate={canCreate}
            canDelete={canDelete}
            unitsSearch={unitsSearch}
            unitsMissingEmailOnly={unitsMissingEmailOnly}
            onOpenUnitsSearch={handleOpenUnitsSearch}
            onClearUnitsSearch={handleClearUnitsSearch}
            onClearMissingEmailFilter={handleClearMissingEmailFilter}
            navigate={navigate}
            propertyId={propertyId}
            lpe1UnitIds={lpe1UnitIds}
            onExport={handleExportUnits}
            onDeleteUnit={handleDeleteUnit}
            onImport={async (file) => {
              if (propertyActionsDisabled) return;
              setImportPreviewError("");
              try {
                const existingUnitsResponse = await getUnitsByProperty(propertyId);
                const existingUnits = Array.isArray(existingUnitsResponse)
                  ? existingUnitsResponse
                  : existingUnitsResponse.results || [];
                const preview = await previewImportUnits(propertyId, file);
                const rowsWithErrors = applyImportUniqErrors(
                  preview?.rows || [],
                  existingUnits
                );
                setImportFile(file);
                setImportRows(rowsWithErrors);
                setImportErrorCount(
                  rowsWithErrors.filter((row) => row.error).length
                );
                setImportPreviewOpen(true);
              } catch (err) {
                console.error(err);
                const message =
                  err?.response?.data?.error ||
                  err?.message ||
                  "Failed to preview import file.";
                setImportFile(null);
                setImportRows([]);
                setImportErrorCount(0);
                setImportPreviewError(message);
                setImportPreviewOpen(true);
              }
            }}
            showToast={showToast}
            actionsDisabled={propertyActionsDisabled}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <SectionTickets
            tickets={tickets}
            ticketsPage={ticketsPage}
            ticketsTotal={ticketsTotal}
            ticketsPageSize={TICKETS_PAGE_SIZE}
            onTicketsPageChange={(event, page) => setTicketsPage(page)}
            propertyId={propertyId}
            showResolvedClosed={showResolvedClosed}
            onToggleResolvedClosed={() => setShowResolvedClosed((prev) => !prev)}
            ticketsError={ticketsError}
            ticketsLoading={ticketsLoading}
            canCreate={canCreate}
            onCreateTicket={handleOpenTicketCreate}
            actionsDisabled={propertyActionsDisabled}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <SectionCard
            title="Insurance Policies"
            items={property.insurance_policies}
            canManage={canEdit || canCreate || canDelete}
            actionsDisabled={propertyActionsDisabled}
            manageLink={`/properties/${propertyId}/insurance`}
            columns={[
              ["Policy #", "policy_number"],
              ["Provider", "provider_name"],
              ["Coverage", "coverage_type"],
              ["Renewal", (row) => formatDate(row.renewal_date)],
            ]}
            dateField="renewal_date"
          />
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          <SectionCard
            title="Utilities"
            items={property.utilities}
            canManage={canEdit || canCreate || canDelete}
            actionsDisabled={propertyActionsDisabled}
            manageLink={`/properties/${propertyId}/utilities`}
            columns={[
              ["Type", "utility_type"],
              ["Supplier", "supplier_name"],
              ["Email", "email_address"],
              ["Contact", "contact_number"],
              ["Meter #", "meter_number"],
            ]}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={4}>
          <SectionCard
            title="Assessments"
            items={assessments}
            canManage={canEdit || canCreate || canDelete}
            actionsDisabled={propertyActionsDisabled}
            manageLink={`/properties/${propertyId}/assessments`}
            columns={[
              ["Type", (row) => getAssessmentTypeName(row.assessment_type_id)],
              ["Pass", (row) => (
                <Chip label={row.passed ? "Yes" : "No"} color={row.passed ? "success" : "error"} size="small" />
              )],
              ["Assessment Date", (row) => formatDate(row.assessment_date)],
              ["Expiry", (row) => formatDate(row.expiry_date)],
            ]}
            dateField="expiry_date"
            statusResolver={getAssessmentStatus}
            loading={assessmentsLoading}
            error={assessmentsError}
            page={assessmentsPage}
            total={assessmentsTotal}
            pageSize={ASSESSMENTS_PAGE_SIZE}
            onPageChange={(event, page) => setAssessmentsPage(page)}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={5}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 4 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Property Enquiries Defaults
            </Typography>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={4}>
                <Paper
                  elevation={1}
                  onClick={() => navigate(`/properties/${propertyId}/metadata/lpe1`)}
                  sx={{
                    p: 2,
                    cursor: "pointer",
                    borderRadius: 2,
                    border: "1px solid #e0e0e0",
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={600}>
                    LEASEHOLD PROPERTY ENQUIRIES
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    LPE1
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper
                  elevation={1}
                  onClick={() => navigate(`/properties/${propertyId}/metadata/fme1`)}
                  sx={{
                    p: 2,
                    cursor: "pointer",
                    borderRadius: 2,
                    border: "1px solid #e0e0e0",
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={600}>
                    FREEHOLD MANAGEMENT ENQUIRIES
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    FME1
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Paper>
          <SectionLPE1
            lpe1Forms={lpe1Forms}
            lpe1Page={lpe1Page}
            lpe1Total={lpe1Total}
            lpe1PageSize={PROPERTY_ENQUIRIES_PAGE_SIZE}
            lpe1Loading={lpe1Loading}
            lpe1Error={lpe1Error}
            onLpe1PageChange={(event, page) => setLpe1Page(page)}
            navigate={navigate}
            location={location}
            formatDate={formatDate}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={6}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 4 }}>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  Communal Fire Doors
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Record each communal fire door so it can be included in fire door compliance checks.
                </Typography>
              </Box>

              {canCreate && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleOpenCommunalFireDoorCreate}
                  disabled={propertyActionsDisabled}
                >
                  Add Fire Door
                </Button>
              )}
            </Box>

            {communalFireDoors.length ? (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Building Number/Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Floor</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Identification</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {communalFireDoors.map((fireDoor) => (
                        <TableRow key={fireDoor.id}>
                          <TableCell>{fireDoor.building_name}</TableCell>
                          <TableCell>{fireDoor.floor}</TableCell>
                          <TableCell>{fireDoor.door_type_label}</TableCell>
                          <TableCell>{fireDoor.identification}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {communalFireDoorTotal > FIRE_DOOR_PAGE_SIZE && (
                  <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                    <Pagination
                      count={Math.ceil(communalFireDoorTotal / FIRE_DOOR_PAGE_SIZE)}
                      page={communalFireDoorPage}
                      onChange={(event, page) => setCommunalFireDoorPage(page)}
                    />
                  </Box>
                )}
              </>
            ) : (
              <Box
                sx={{
                  border: "1px dashed #d6d6d6",
                  borderRadius: 2,
                  p: 3,
                  textAlign: "center",
                }}
              >
                <Typography fontWeight={600}>No communal fire doors</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                  Add the doors for this property before completing a communal fire door checklist.
                </Typography>
              </Box>
            )}
          </Paper>

          <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 4 }}>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  Compliance Schedules
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Configure recurring compliance work by schedule.
                </Typography>
              </Box>

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
                {canCreate && (
                  <Button
                    variant="contained"
                    onClick={handleOpenComplianceScheduleCreate}
                    disabled={propertyActionsDisabled}
                  >
                    Add Compliance Schedule
                  </Button>
                )}
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>Alert Scope</InputLabel>
                  <Select
                    label="Alert Scope"
                    value={complianceAlertScope}
                    onChange={(e) => setComplianceAlertScope(e.target.value)}
                  >
                    <MenuItem value="due_soon">Due Within 7 Days</MenuItem>
                    <MenuItem value="all">All Schedules</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {complianceSchedules.length ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Frequency</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Due Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Urgency</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Checked</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {complianceSchedules.map((schedule) => (
                      <TableRow
                        key={schedule.id}
                        id={`compliance-schedule-row-${schedule.id}`}
                      >
                        <TableCell>
                          {COMPLIANCE_TYPE_OPTIONS.find(
                            (o) => o.value === String(schedule.compliance_type_id)
                          )?.label ||
                            schedule.compliance_type_name}
                        </TableCell>
                        <TableCell>{getComplianceFrequencyLabel(schedule.frequency)}</TableCell>
                        <TableCell>{formatDate(schedule.next_due_date)}</TableCell>
                        <TableCell>
                          <Chip
                            label={getScheduleUrgency(schedule).label}
                            color={getScheduleUrgency(schedule).color}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{schedule.checked ? "Yes" : "No"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box
                sx={{
                  border: "1px dashed #d6d6d6",
                  borderRadius: 2,
                  p: 3,
                  textAlign: "center",
                }}
              >
                <Typography fontWeight={600}>No compliance schedules</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                  No compliance schedules have been configured for this property yet.
                </Typography>
              </Box>
            )}
          </Paper>

          <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 4 }}>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  Compliance Tasks
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Showing {complianceTasks.length} of {complianceTotal} tasks
                </Typography>
              </Box>

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
                {canCreate && (
                  <Button
                    variant="contained"
                    onClick={handleOpenComplianceCreate}
                    disabled={propertyActionsDisabled || !complianceSchedules.length}
                  >
                    Add Compliance Task
                  </Button>
                )}
                <FormControl sx={{ minWidth: 220 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    label="Type"
                    value={complianceTypeFilter}
                    onChange={(e) => setComplianceTypeFilter(e.target.value)}
                  >
                    <MenuItem value="all">All</MenuItem>
                    {COMPLIANCE_TYPE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {complianceError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {complianceError}
              </Alert>
            )}

            {complianceLoading ? (
              <Typography>Loading compliance tasks...</Typography>
            ) : complianceTasks.length ? (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Frequency</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Due Date</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Inspected</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Result</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Completed By</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Document</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {complianceTasks.map((task) => (
                        <TableRow key={task.id}>
                          <TableCell>
                            {getComplianceTypeLabel(
                              task.compliance_type_id,
                              task.compliance_type_name
                            )}
                          </TableCell>
                          <TableCell>{getComplianceFrequencyLabel(task.frequency)}</TableCell>
                          <TableCell>{formatDate(task.due_date)}</TableCell>
                          <TableCell>{formatDate(task.date_inspected)}</TableCell>
                          <TableCell>{task.result || "-"}</TableCell>
                          <TableCell>
                            {task.completed_by_name || task.completed_by_email || "-"}
                          </TableCell>
                          <TableCell>
                            {task.document_download_url ? (
                                <Button
                                  disabled={propertyActionsDisabled}
                                  size="small"
                                  component="a"
                                href={task.document_download_url}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {task.document_file_name || "Download"}
                              </Button>
                            ) : (
                              "-"
                            )}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                              {isCommunalFireDoorTask(task) && task.fire_door_checklist?.length ? (
                                <Button
                                  size="small"
                                  onClick={() => handleOpenComplianceViewChecklist(task)}
                                >
                                  View Checklist
                                </Button>
                              ) : null}
                              {canEdit && (
                                <Button
                                  size="small"
                                  onClick={() => handleOpenComplianceEdit(task)}
                                  disabled={propertyActionsDisabled}
                                >
                                  Edit
                                </Button>
                              )}
                              {canDelete && (
                                <Button
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteComplianceTask(task)}
                                  disabled={propertyActionsDisabled}
                                >
                                  Delete
                                </Button>
                              )}
                              {!isCommunalFireDoorTask(task) && !canEdit && !canDelete && "-"}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {complianceTotal > COMPLIANCE_PAGE_SIZE && (
                  <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                    <Pagination
                      count={Math.ceil(complianceTotal / COMPLIANCE_PAGE_SIZE)}
                      page={compliancePage}
                      onChange={(event, page) => setCompliancePage(page)}
                    />
                  </Box>
                )}
              </>
            ) : (
              <Box
                sx={{
                  border: "1px dashed #d6d6d6",
                  borderRadius: 2,
                  p: 3,
                  textAlign: "center",
                }}
              >
                <Typography fontWeight={600}>No compliance tasks found</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                  Create a task against an existing compliance schedule.
                </Typography>
                {canCreate && (
                  <Button
                    variant="outlined"
                    onClick={handleOpenComplianceCreate}
                    disabled={propertyActionsDisabled || !complianceSchedules.length}
                  >
                    Add Compliance Task
                  </Button>
                )}
              </Box>
            )}
          </Paper>
        </TabPanel>

        <TabPanel value={activeTab} index={7}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>
                Notes
              </Typography>
              {canEdit && (
                <Button
                  variant="contained"
                  onClick={handleSaveNotes}
                  disabled={
                    propertyActionsDisabled ||
                    notesSaving ||
                    (notesDraft || "") === (property?.metadata?.notes || "")
                  }
                >
                  {notesSaving ? "Saving..." : "Save Notes"}
                </Button>
              )}
            </Box>
            <TextField
              fullWidth
              multiline
              minRows={6}
              placeholder="Add codes or other useful site info..."
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
              InputProps={{ readOnly: !canEdit }}
            />
            {notesError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {notesError}
              </Alert>
            )}
          </Paper>
        </TabPanel>
      </Box>

      <Dialog
        open={ticketCreateOpen}
        onClose={() => {
          if (!ticketCreateSubmitting) {
            setTicketCreateOpen(false);
            setTicketCreateError("");
          }
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create Ticket</DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          <Stack spacing={2}>
            {ticketCreateError && (
              <Alert severity="error">{ticketCreateError}</Alert>
            )}
            <TextField
              fullWidth
              required
              label="Title"
              value={ticketCreateForm.title}
              onChange={(e) =>
                setTicketCreateForm((prev) => ({
                  ...prev,
                  title: e.target.value,
                }))
              }
            />
            <TextField
              fullWidth
              required
              multiline
              minRows={4}
              label="Description"
              value={ticketCreateForm.description}
              onChange={(e) =>
                setTicketCreateForm((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
            />
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
              }}
            >
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  label="Priority"
                  value={ticketCreateForm.priority}
                  onChange={(e) =>
                    setTicketCreateForm((prev) => ({
                      ...prev,
                      priority: e.target.value,
                    }))
                  }
                >
                  {TICKET_PRIORITY_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth required>
                <InputLabel>Type</InputLabel>
                <Select
                  label="Type"
                  value={ticketCreateForm.type}
                  onChange={(e) =>
                    setTicketCreateForm((prev) => ({
                      ...prev,
                      type: e.target.value,
                    }))
                  }
                >
                  <MenuItem value="" disabled>
                    <em>Select type</em>
                  </MenuItem>
                  {TICKET_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <TextField
              fullWidth
              required
              label="Requester Name"
              value={ticketCreateForm.requester_name}
              onChange={(e) =>
                setTicketCreateForm((prev) => ({
                  ...prev,
                  requester_name: e.target.value,
                }))
              }
            />
            <Typography variant="body2" color="text.secondary">
              Property: {property?.name || `Property ${propertyId}`}
            </Typography>
            {ticketCreateForm.type === "Management Pack" && (
              <Autocomplete
                options={ticketCreatePropertyEnquiryOptions}
                getOptionLabel={(option) => option?.label || ""}
                isOptionEqualToValue={(option, value) => (
                  option?.id === value?.id && option?.form_type === value?.form_type
                )}
                value={ticketCreatePropertyEnquiryForm}
                loading={ticketCreatePropertyEnquiryLoading}
                onChange={(_, value) => setTicketCreatePropertyEnquiryForm(value)}
                noOptionsText="No property enquiries found."
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Property Enquiry Form"
                    placeholder="Select an LPE1 or FME1 questionnaire"
                  />
                )}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setTicketCreateOpen(false)}
            disabled={ticketCreateSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateTicket}
            disabled={
              propertyActionsDisabled ||
              ticketCreateSubmitting ||
              !ticketCreateForm.type
            }
          >
            {ticketCreateSubmitting ? "Creating..." : "Create Ticket"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* COMPLIANCE CREATE DIALOG */}
      <Dialog
        open={complianceCreateOpen}
        onClose={() => {
          setComplianceCreateChecklistDialogOpen(false);
          setComplianceCreateOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Compliance Task</DialogTitle>
        <DialogContent sx={{ width: "100%" }}>
          {!complianceSchedules.length && (
            <Alert severity="info" sx={{ mt: 2 }}>
              No compliance schedules are available for this property.
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1, width: "100%" }}>
            <Grid item xs={12} sx={{ width: "100%" }}>
              <FormControl fullWidth required>
                <InputLabel>Compliance Schedule</InputLabel>
                <Select
                  label="Compliance Schedule"
                  value={complianceCreateForm.schedule_id}
                  onChange={(e) => {
                    const nextScheduleId = e.target.value;
                    const nextSchedule = complianceSchedules.find(
                      (schedule) => String(schedule.id) === String(nextScheduleId)
                    );
                    setComplianceCreateForm((prev) => ({
                      ...prev,
                      schedule_id: nextScheduleId,
                      fire_door_checklist:
                        nextSchedule?.requires_fire_door_checklist
                          ? buildDefaultFireDoorChecklist(prev.fire_door_checklist)
                          : [],
                    }));
                    if (!nextSchedule?.requires_fire_door_checklist) {
                      setComplianceCreateChecklistOpen(false);
                      setComplianceCreateChecklistDialogOpen(false);
                    }
                  }}
                >
                  {complianceSchedules.map((schedule) => (
                    <MenuItem key={schedule.id} value={String(schedule.id)}>
                      {`${schedule.compliance_type_name} - ${getComplianceFrequencyLabel(schedule.frequency)}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sx={{ width: "100%" }}>
              <TextField
                label="Date Inspected"
                type="date"
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                value={complianceCreateForm.date_inspected}
                onChange={(e) =>
                  setComplianceCreateForm((prev) => ({
                    ...prev,
                    date_inspected: e.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sx={{ width: "100%" }}>
              <FormControl fullWidth required>
                <InputLabel>Result</InputLabel>
                <Select
                  label="Result"
                  value={complianceCreateForm.result}
                  onChange={(e) =>
                    setComplianceCreateForm((prev) => ({
                      ...prev,
                      result: e.target.value,
                    }))
                  }
                >
                  <MenuItem value="Pass">Pass</MenuItem>
                  <MenuItem value="Fail">Fail</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sx={{ width: "100%" }}>
              <TextField
                label="Completed By"
                fullWidth
                required
                value={complianceCreateForm.completed_by_name}
                onChange={(e) =>
                  setComplianceCreateForm((prev) => ({
                    ...prev,
                    completed_by_name: e.target.value,
                  }))
                }
              />
            </Grid>
            {complianceCreateForm.result === "Fail" && (
              <Grid item xs={12} sx={{ width: "100%" }}>
                <TextField
                  label="Failure Notes"
                  fullWidth
                  multiline
                  minRows={3}
                  value={complianceCreateForm.fail_notes}
                  onChange={(e) =>
                    setComplianceCreateForm((prev) => ({
                      ...prev,
                      fail_notes: e.target.value,
                    }))
                  }
                />
              </Grid>
            )}
            <Grid item xs={12} sx={{ width: "100%" }}>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
                <Button
                  component="label"
                  variant="outlined"
                  disabled={propertyActionsDisabled}
                >
                  {complianceCreateForm.document ? "Change Document" : "Attach Document"}
                  <input
                    type="file"
                    hidden
                    onChange={(e) =>
                      setComplianceCreateForm((prev) => ({
                        ...prev,
                        document: e.target.files?.[0] || null,
                      }))
                    }
                  />
                </Button>
                {createChecklistEnabled && (
                  <Button
                    variant="outlined"
                    onClick={handleOpenComplianceCreateChecklist}
                    disabled={propertyActionsDisabled || !communalFireDoorOptions.length}
                  >
                    {complianceCreateChecklistOpen ? "Edit Checklist" : "Add Checklist"}
                  </Button>
                )}
                {createChecklistEnabled && complianceCreateChecklistOpen && (
                  <Button
                    variant="text"
                    color="inherit"
                    onClick={handleRemoveComplianceCreateChecklist}
                    disabled={propertyActionsDisabled || complianceCreateSubmitting}
                  >
                    Remove Checklist
                  </Button>
                )}
              </Box>
              {complianceCreateForm.document && (
                <Typography variant="caption" sx={{ display: "block", mt: 1 }}>
                  {complianceCreateForm.document.name}
                </Typography>
              )}
              {renderChecklistStatus(
                createChecklistEnabled,
                complianceCreateChecklistOpen,
                complianceCreateForm.fire_door_checklist
              )}
            </Grid>
            <Grid item xs={12} sx={{ width: "100%" }}>
              <Typography variant="body2" color="text.secondary">
                Property: {property?.name || "—"}
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setComplianceCreateChecklistDialogOpen(false);
              setComplianceCreateOpen(false);
            }}
            disabled={complianceCreateSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleComplianceCreate}
            disabled={
              propertyActionsDisabled ||
              complianceCreateSubmitting ||
              !complianceSchedules.length
            }
          >
            {complianceCreateSubmitting ? "Saving..." : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={complianceCreateChecklistDialogOpen}
        onClose={() => setComplianceCreateChecklistDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        fullScreen={checklistDialogFullScreen}
      >
        <DialogTitle>Create Fire Door Checklist</DialogTitle>
        <DialogContent dividers sx={{ width: "100%" }}>
          {renderFireDoorChecklist(
            complianceCreateForm.fire_door_checklist,
            setComplianceCreateForm,
            propertyActionsDisabled || complianceCreateSubmitting
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setComplianceCreateChecklistDialogOpen(false)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={communalFireDoorOpen}
        onClose={() => setCommunalFireDoorOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Fire Door</DialogTitle>
        <DialogContent sx={{ width: "100%" }}>
          <Grid container spacing={2} sx={{ mt: 1, width: "100%" }}>
            <Grid item xs={12} sx={{ width: "100%" }}>
              <TextField
                label="Building Number/Name"
                fullWidth
                required
                value={communalFireDoorForm.building_name}
                onChange={(e) =>
                  setCommunalFireDoorForm((prev) => ({
                    ...prev,
                    building_name: e.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ width: "100%" }}>
              <TextField
                label="Floor"
                fullWidth
                required
                inputMode="numeric"
                value={communalFireDoorForm.floor}
                onChange={(e) =>
                  setCommunalFireDoorForm((prev) => ({
                    ...prev,
                    floor: e.target.value.replace(/[^0-9]/g, ""),
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ width: "100%" }}>
              <FormControl fullWidth required>
                <InputLabel>Type</InputLabel>
                <Select
                  label="Type"
                  value={communalFireDoorForm.door_type}
                  onChange={(e) =>
                    setCommunalFireDoorForm((prev) => ({
                      ...prev,
                      door_type: e.target.value,
                    }))
                  }
                >
                  {FIRE_DOOR_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sx={{ width: "100%" }}>
              <TextField
                label="Identification"
                fullWidth
                required
                value={communalFireDoorForm.identification}
                onChange={(e) =>
                  setCommunalFireDoorForm((prev) => ({
                    ...prev,
                    identification: e.target.value,
                  }))
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommunalFireDoorOpen(false)} disabled={communalFireDoorSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCommunalFireDoorCreate}
            disabled={propertyActionsDisabled || communalFireDoorSubmitting}
          >
            {communalFireDoorSubmitting ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={complianceScheduleOpen}
        onClose={() => setComplianceScheduleOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Compliance Schedule</DialogTitle>
        <DialogContent sx={{ width: "100%" }}>
          <Grid container spacing={2} sx={{ mt: 1, width: "100%" }}>
            <Grid item xs={12} sx={{ width: "100%" }}>
              <FormControl fullWidth required>
                <InputLabel>Compliance Type</InputLabel>
                <Select
                  label="Compliance Type"
                  value={complianceScheduleForm.compliance_type_id}
                  onChange={(e) =>
                    setComplianceScheduleForm((prev) => ({
                      ...prev,
                      compliance_type_id: e.target.value,
                    }))
                  }
                >
                  {COMPLIANCE_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sx={{ width: "100%" }}>
              <FormControl fullWidth required>
                <InputLabel>Frequency</InputLabel>
                <Select
                  label="Frequency"
                  value={complianceScheduleForm.frequency}
                  onChange={(e) =>
                    setComplianceScheduleForm((prev) => ({
                      ...prev,
                      frequency: e.target.value,
                    }))
                  }
                >
                  {COMPLIANCE_FREQUENCY_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sx={{ width: "100%" }}>
              <Typography variant="body2" color="text.secondary">
                Property: {property?.name || "—"}
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setComplianceScheduleOpen(false)} disabled={complianceScheduleSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleComplianceScheduleCreate}
            disabled={propertyActionsDisabled || complianceScheduleSubmitting}
          >
            {complianceScheduleSubmitting ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* COMPLIANCE EDIT DIALOG */}
      <Dialog
        open={complianceEditOpen}
        onClose={() => {
          setComplianceEditChecklistDialogOpen(false);
          setComplianceEditOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Compliance Task</DialogTitle>
        <DialogContent sx={{ width: "100%" }}>
          <Grid container spacing={2} sx={{ mt: 1, width: "100%" }}>
            <Grid item xs={12} sx={{ width: "100%" }}>
              <FormControl fullWidth required>
                <InputLabel>Compliance Type</InputLabel>
                <Select
                  label="Compliance Type"
                  value={complianceEditForm.compliance_type_id}
                  onChange={(e) => {
                    const nextTypeId = e.target.value;
                    const nextType = COMPLIANCE_TYPE_OPTIONS.find(
                      (option) => option.value === String(nextTypeId)
                    );
                    setComplianceEditForm((prev) => ({
                      ...prev,
                      compliance_type_id: nextTypeId,
                      fire_door_checklist:
                        nextType?.requiresFireDoorChecklist
                          ? buildDefaultFireDoorChecklist(prev.fire_door_checklist)
                          : [],
                    }));
                    if (!nextType?.requiresFireDoorChecklist) {
                      setComplianceEditChecklistOpen(false);
                      setComplianceEditChecklistDialogOpen(false);
                    }
                  }}
                >
                  {COMPLIANCE_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sx={{ width: "100%" }}>
              <FormControl fullWidth required>
                <InputLabel>Frequency</InputLabel>
                <Select
                  label="Frequency"
                  value={complianceEditForm.frequency}
                  onChange={(e) =>
                    setComplianceEditForm((prev) => ({
                      ...prev,
                      frequency: e.target.value,
                    }))
                  }
                >
                  {COMPLIANCE_FREQUENCY_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sx={{ width: "100%" }}>
              <TextField
                label="Due Date"
                type="date"
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                value={complianceEditForm.due_date}
                onChange={(e) =>
                  setComplianceEditForm((prev) => ({
                    ...prev,
                    due_date: e.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sx={{ width: "100%" }}>
              <TextField
                label="Date Inspected"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={complianceEditForm.date_inspected}
                onChange={(e) =>
                  setComplianceEditForm((prev) => ({
                    ...prev,
                    date_inspected: e.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sx={{ width: "100%" }}>
              <FormControl fullWidth required>
                <InputLabel>Result</InputLabel>
                <Select
                  label="Result"
                  value={complianceEditForm.result}
                  onChange={(e) =>
                    setComplianceEditForm((prev) => ({
                      ...prev,
                      result: e.target.value,
                    }))
                  }
                >
                  <MenuItem value="Pass">Pass</MenuItem>
                  <MenuItem value="Fail">Fail</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sx={{ width: "100%" }}>
              <TextField
                label="Failure Notes"
                fullWidth
                multiline
                minRows={3}
                value={complianceEditForm.fail_notes}
                onChange={(e) =>
                  setComplianceEditForm((prev) => ({
                    ...prev,
                    fail_notes: e.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sx={{ width: "100%" }}>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
                <Button
                  component="label"
                  variant="outlined"
                  disabled={propertyActionsDisabled}
                >
                  {complianceEditForm.document
                    ? "Change Document"
                    : complianceSelectedTask?.document_file_name
                    ? "Replace Document"
                    : "Attach Document"}
                  <input
                    type="file"
                    hidden
                    onChange={(e) =>
                      setComplianceEditForm((prev) => ({
                        ...prev,
                        document: e.target.files?.[0] || null,
                      }))
                    }
                  />
                </Button>
                {editChecklistEnabled && (
                  <Button
                    variant="outlined"
                    onClick={handleOpenComplianceEditChecklist}
                    disabled={propertyActionsDisabled || !communalFireDoorOptions.length}
                  >
                    {complianceEditChecklistOpen ? "Edit Checklist" : "Add Checklist"}
                  </Button>
                )}
                {editChecklistEnabled && complianceEditChecklistOpen && (
                  <Button
                    variant="text"
                    color="inherit"
                    onClick={handleRemoveComplianceEditChecklist}
                    disabled={propertyActionsDisabled || complianceEditSubmitting}
                  >
                    Remove Checklist
                  </Button>
                )}
              </Box>
              {complianceEditForm.document && (
                <Typography variant="caption" sx={{ display: "block", mt: 1 }}>
                  {complianceEditForm.document.name}
                </Typography>
              )}
              {!complianceEditForm.document && complianceSelectedTask?.document_file_name && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                  {complianceSelectedTask.document_file_name}
                </Typography>
              )}
              {renderChecklistStatus(
                editChecklistEnabled,
                complianceEditChecklistOpen,
                complianceEditForm.fire_door_checklist
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setComplianceEditChecklistDialogOpen(false);
              setComplianceEditOpen(false);
            }}
            disabled={complianceEditSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleComplianceEdit}
            disabled={propertyActionsDisabled || complianceEditSubmitting}
          >
            {complianceEditSubmitting ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={complianceEditChecklistDialogOpen}
        onClose={() => setComplianceEditChecklistDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        fullScreen={checklistDialogFullScreen}
      >
        <DialogTitle>Edit Fire Door Checklist</DialogTitle>
        <DialogContent dividers sx={{ width: "100%" }}>
          {renderFireDoorChecklist(
            complianceEditForm.fire_door_checklist,
            setComplianceEditForm,
            propertyActionsDisabled || complianceEditSubmitting
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setComplianceEditChecklistDialogOpen(false)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={complianceViewChecklistDialogOpen}
        onClose={() => setComplianceViewChecklistDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        fullScreen={checklistDialogFullScreen}
      >
        <DialogTitle>
          View Fire Door Checklist
          {complianceViewChecklistTask?.due_date
            ? ` - ${formatDate(complianceViewChecklistTask.due_date)}`
            : ""}
        </DialogTitle>
        <DialogContent dividers sx={{ width: "100%" }}>
          {renderFireDoorChecklist(complianceViewChecklistRows, setComplianceViewChecklistRows, true)}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setComplianceViewChecklistDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {/* TOAST / SNACKBAR */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((t) => ({ ...t, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={importPreviewOpen}
        onClose={handleCancelImport}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Preview Units Import</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Box sx={{ maxHeight: 420, overflow: "auto", p: 2 }}>
            {importPreviewError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {importPreviewError}
              </Alert>
            )}
            {importErrorCount > 0 && !importPreviewError && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {importErrorCount === importRows.length
                  ? "All rows have errors, please correct and re-upload."
                  : `${importErrorCount} row${importErrorCount === 1 ? "" : "s"} will be skipped due to errors.`}
              </Alert>
            )}
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {importErrorCount > 0 && <TableCell>Error</TableCell>}
                  <TableCell>Unit Name</TableCell>
                  <TableCell>Owner Name</TableCell>
                  <TableCell>Mobile</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Floor</TableCell>
                  <TableCell>Square Feet</TableCell>
                  <TableCell>Address Line 1</TableCell>
                  <TableCell>Address Line 2</TableCell>
                  <TableCell>City</TableCell>
                  <TableCell>County</TableCell>
                  <TableCell>Postcode</TableCell>
                  <TableCell>Different Correspondence?</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {importRows.map((row, idx) => (
                  <TableRow key={`${row.unit_name}-${idx}`}>
                    {importErrorCount > 0 && (
                      <TableCell
                        sx={{
                          color: row.error ? "error.main" : "text.secondary",
                          fontWeight: row.error ? 700 : 400,
                        }}
                      >
                        {formatPreviewValue(row.error)}
                      </TableCell>
                    )}
                    <TableCell>{formatPreviewValue(row.unit_name)}</TableCell>
                    <TableCell>{formatPreviewValue(row.owner_name)}</TableCell>
                    <TableCell>{formatPreviewValue(row.mobile)}</TableCell>
                    <TableCell>{formatPreviewValue(row.email)}</TableCell>
                    <TableCell>{formatPreviewValue(row.floor)}</TableCell>
                    <TableCell>{formatPreviewValue(row.square_feet)}</TableCell>
                    <TableCell>{formatPreviewValue(row.address_line1)}</TableCell>
                    <TableCell>{formatPreviewValue(row.address_line2)}</TableCell>
                    <TableCell>{formatPreviewValue(row.city)}</TableCell>
                    <TableCell>{formatPreviewValue(row.county)}</TableCell>
                    <TableCell>{formatPreviewValue(row.postcode)}</TableCell>
                    <TableCell>{row.has_different_correspondence_address ? "✓" : ""}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCancelImport} disabled={importSubmitting}>
            Cancel
          </Button>
          {importErrorCount > 0 && importErrorCount < importRows.length && !importPreviewError && (
            <Button
              variant="outlined"
              onClick={handleImportAnyway}
              disabled={propertyActionsDisabled || importSubmitting}
            >
              Import anyway
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleConfirmImport}
            disabled={
              propertyActionsDisabled ||
              importSubmitting ||
              importErrorCount > 0 ||
              !!importPreviewError
            }
          >
            {importSubmitting ? "Importing..." : "Import Units"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={groupCreateOpen}
        onClose={() => setGroupCreateOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Create Unit Group</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label="Group name"
            value={groupCreateName}
            onChange={(e) => setGroupCreateName(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setGroupCreateOpen(false)} disabled={groupCreateSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateGroup}
            disabled={
              propertyActionsDisabled ||
              groupCreateSubmitting ||
              !groupCreateName.trim()
            }
          >
            {groupCreateSubmitting ? "Creating..." : "Create Group"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={groupPickerOpen}
        onClose={handleCloseGroupPicker}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Add Units to {groupPickerGroup?.name || "Group"}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label="Search units"
              value={groupPickerSearch}
              onChange={(e) => {
                setGroupPickerSearch(e.target.value);
                setGroupPickerPage(1);
              }}
            />
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Unit</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Mobile</TableCell>
                <TableCell>Address</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groupPickerLoading ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Skeleton height={40} />
                  </TableCell>
                </TableRow>
              ) : groupPickerUnits.length ? (
                groupPickerUnits.map((unit) => (
                  <TableRow key={unit.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={groupPickerSelected.includes(unit.id)}
                        onChange={() => handleTogglePickerUnit(unit.id)}
                      />
                    </TableCell>
                    <TableCell>
                      {unit.unit_name || unit.unit_code || `Unit ${unit.id}`}
                    </TableCell>
                    <TableCell>{unit.owner_name || "-"}</TableCell>
                    <TableCell>{unit.email || "-"}</TableCell>
                    <TableCell>{unit.mobile || "-"}</TableCell>
                    <TableCell>
                      {[unit.address_line1, unit.address_line2, unit.city, unit.postcode]
                        .filter(Boolean)
                        .join(", ") || "-"}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography color="text.secondary">
                      No matching units available.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {groupPickerTotal > GROUP_PICKER_PAGE_SIZE && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Pagination
                count={Math.ceil(groupPickerTotal / GROUP_PICKER_PAGE_SIZE)}
                page={groupPickerPage}
                onChange={(event, page) => setGroupPickerPage(page)}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseGroupPicker}>Close</Button>
          <Button
            variant="contained"
            onClick={handleAddUnitsToGroup}
            disabled={propertyActionsDisabled || !groupPickerSelected.length}
          >
            Add Selected
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={directorDialogOpen}
        onClose={() => {
          if (!directorSubmitting) {
            setDirectorDialogOpen(false);
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{editingDirector ? "Edit Director" : "Add Director"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label="Name"
              value={directorForm.name}
              onChange={(e) =>
                setDirectorForm((prev) => ({ ...prev, name: e.target.value }))
              }
              required
              fullWidth
              disabled={directorSubmitting}
            />
            <TextField
              label="Email"
              value={directorForm.email}
              onChange={(e) =>
                setDirectorForm((prev) => ({ ...prev, email: e.target.value }))
              }
              fullWidth
              disabled={directorSubmitting}
            />
            <TextField
              label="Phone"
              value={directorForm.phone}
              onChange={(e) =>
                setDirectorForm((prev) => ({ ...prev, phone: e.target.value }))
              }
              fullWidth
              disabled={directorSubmitting}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDirectorDialogOpen(false)} disabled={directorSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveDirector}
            variant="contained"
            disabled={propertyActionsDisabled || directorSubmitting}
          >
            {directorSubmitting ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={locationDialogOpen}
        onClose={() => setLocationDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Find Property Location</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Postcode"
                value={locationPostcode}
                onChange={(e) => setLocationPostcode(e.target.value)}
                disabled={locationLoading}
                fullWidth
              />
              <TextField
                label="Filter (optional)"
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                disabled={locationLoading}
                fullWidth
              />
            </Stack>
            <Button
              variant="outlined"
              onClick={handleLocationLookup}
              disabled={propertyActionsDisabled || locationLoading}
              startIcon={<SearchIcon />}
              sx={{ alignSelf: "flex-start", minWidth: 180 }}
            >
              {locationLoading ? "Searching..." : "Find Address"}
            </Button>
            {locationError && <Alert severity="error">{locationError}</Alert>}
            <Collapse in={locationResults.length > 0} timeout={300}>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {locationResults.length}{" "}
                {locationResults.length === 1 ? "address" : "addresses"} found.
                Choose one from the dropdown.
              </Typography>
              <FormControl fullWidth sx={{ mt: 1 }}>
                <InputLabel>Select Address</InputLabel>
                <Select
                  value={locationSelected}
                  label="Select Address"
                  onChange={(e) => handleLocationSelect(e.target.value)}
                >
                  {locationResults.map((addr, index) => {
                    const label = addr.address || [addr.id].filter(Boolean).join(", ");
                    return (
                      <MenuItem key={`${index}-${label}`} value={String(index)}>
                        {label}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Collapse>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setLocationDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={unitsSearchOpen}
        onClose={() => setUnitsSearchOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Search Units</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            fullWidth
            label="Search"
            value={unitsSearchDraft}
            onChange={(e) => setUnitsSearchDraft(e.target.value)}
            placeholder="Search by unit, owner, email, phone, address..."
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setUnitsSearchOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleApplyUnitsSearch}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}

/* ---------------- Reusable Sections ---------------- */

function SectionUnitGroups({
  groups,
  canCreate,
  canDelete,
  actionsDisabled = false,
  expandedGroupId,
  groupUnitsById,
  groupUnitsTotalById,
  groupUnitsPageById,
  groupUnitsLoadingById,
  groupUnitsPageSize,
  onToggleExpand,
  onGroupPageChange,
  onAddUnits,
  onEmailGroup,
  onRemoveUnit,
  onDeleteGroup,
  onNavigateUnit,
  onCreateGroup,
}) {
  return (
    <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 4 }}>
      <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={600}>Unit Groups</Typography>
        {canCreate && (
          <Button
            variant="outlined"
            onClick={onCreateGroup}
            disabled={actionsDisabled}
          >
            New Group
          </Button>
        )}
      </Grid>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Group</TableCell>
            <TableCell align="right">Units</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.map((group) => {
            const isExpanded = expandedGroupId === group.id;
            const groupUnits = groupUnitsById[group.id] || [];
            const groupTotal = groupUnitsTotalById[group.id] || 0;
            const groupPage = groupUnitsPageById[group.id] || 1;
            const isLoading = groupUnitsLoadingById[group.id];

            return (
              <React.Fragment key={group.id}>
                <TableRow>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Tooltip title={isExpanded ? "Collapse" : "Expand"}>
                        <IconButton size="small" onClick={() => onToggleExpand(group.id)}>
                          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </Tooltip>
                      <Typography fontWeight={600}>{group.name}</Typography>
                      {canCreate && (
                        <Tooltip title="Add units">
                          <IconButton
                            size="small"
                            onClick={() => onAddUnits(group)}
                            disabled={actionsDisabled}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canDelete && group.unit_count === 0 && (
                        <Tooltip title="Delete group">
                          <IconButton
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              onDeleteGroup(group);
                            }}
                            disabled={actionsDisabled}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">{group.unit_count}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={2} sx={{ p: 0, borderBottom: "none" }}>
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Box sx={{ p: 2, borderTop: "1px solid", borderColor: "divider" }}>
                        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                          <Tooltip title={`Email all owners in ${group.name} group`}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<i className="fa-solid fa-envelope" aria-hidden="true" />}
                              onClick={() => onEmailGroup(group)}
                              disabled={actionsDisabled}
                            >
                              Email All
                            </Button>
                          </Tooltip>
                        </Box>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Unit</TableCell>
                              <TableCell>Owner</TableCell>
                              <TableCell>Email</TableCell>
                              <TableCell>Mobile</TableCell>
                              <TableCell align="right"></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {isLoading ? (
                              <TableRow>
                                <TableCell colSpan={5}>
                                  <Skeleton height={32} />
                                </TableCell>
                              </TableRow>
                            ) : groupUnits.length ? (
                              groupUnits.map((unit) => (
                                <TableRow
                                  key={unit.id}
                                  hover
                                  onClick={() => onNavigateUnit(unit.id)}
                                  sx={{ cursor: "pointer" }}
                                >
                                  <TableCell>
                                    {unit.unit_name || unit.unit_code || `Unit ${unit.id}`}
                                  </TableCell>
                                  <TableCell>{unit.owner_name || "-"}</TableCell>
                                  <TableCell>{unit.email || "-"}</TableCell>
                                  <TableCell>{unit.mobile || "-"}</TableCell>
                                  <TableCell align="right">
                                    <Tooltip title="Remove from group">
                                      <IconButton
                                        size="small"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          onRemoveUnit(group.id, unit.id);
                                        }}
                                        disabled={actionsDisabled}
                                      >
                                        <CloseIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={5}>
                                  <Typography color="text.secondary">
                                    No units in this group yet.
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                        {groupTotal > groupUnitsPageSize && (
                          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                            <Pagination
                              count={Math.ceil(groupTotal / groupUnitsPageSize)}
                              page={groupPage}
                              onChange={(event, page) => onGroupPageChange(group.id, page)}
                            />
                          </Box>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </Paper>
  );
}

function SectionUnits({
  units,
  unitsPage,
  unitsTotal,
  unitsPageSize,
  unitsLoading,
  onUnitsPageChange,
  onEmailAllUnits,
  canCreate,
  canDelete,
  unitsSearch,
  unitsMissingEmailOnly,
  onOpenUnitsSearch,
  onClearUnitsSearch,
  onClearMissingEmailFilter,
  navigate,
  propertyId,
  lpe1UnitIds,
  onExport,
  onDeleteUnit,
  onImport,
  showToast,
  actionsDisabled = false,
}) {
  const handleImportClick = () => {
    const input = document.getElementById("unit-import-input");
    if (input) {
      input.click();
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await onImport(file);
    e.target.value = "";
  };

  const handleExportClick = async () => {
    try {
      await onExport?.();
    } catch (err) {
      console.error(err);
      showToast?.("Failed to export units.", "error");
    }
  };

  const lpe1UnitIdSet = new Set(lpe1UnitIds || []);

  return (
    <Paper
      id="units-section"
      elevation={2}
      sx={{ p: 3, borderRadius: 2, mb: 4, overflow: "hidden" }}
    >
      <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={600}>Units</Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            startIcon={<SearchIcon fontSize="small" />}
            onClick={onOpenUnitsSearch}
            disabled={actionsDisabled}
          >
            Search
          </Button>
          <Button
            variant="outlined"
            onClick={onEmailAllUnits}
            startIcon={<i className="fa-solid fa-envelope" aria-hidden="true" />}
            disabled={actionsDisabled}
          >
            Email All
          </Button>
          <Button
            variant="outlined"
            component="a"
            href="/PropertyTemplate.xlsx"
            download
            startIcon={<i className="fa-solid fa-table" aria-hidden="true" />}
            disabled={actionsDisabled}
          >
            Download Template
          </Button>
          <Button
            variant="outlined"
            onClick={handleExportClick}
            startIcon={<i className="fa-solid fa-file-arrow-down" aria-hidden="true" />}
            disabled={actionsDisabled}
          >
            Export
          </Button>
          <Button variant="outlined"
            onClick={handleImportClick}
            startIcon={<i className="fa-solid fa-file-arrow-up" aria-hidden="true" />}
            disabled={actionsDisabled}
          >
            Import
          </Button>
          {canCreate && (
            <Button
              variant="contained"
              onClick={() => navigate(`/properties/${propertyId}/units/create`)}
              disabled={actionsDisabled}
            >
              + Add Unit
            </Button>
          )}
        </Box>
      </Grid>

      {(unitsSearch || unitsMissingEmailOnly) && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          {unitsSearch && (
            <>
              <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                Filter: "{unitsSearch}"
              </Typography>
              <Tooltip title="Remove this filter">
                <IconButton
                  size="small"
                  onClick={onClearUnitsSearch}
                  disabled={actionsDisabled}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
          {unitsMissingEmailOnly && (
            <>
              <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                Filter: units missing a valid email address
              </Typography>
              <Tooltip title="Remove this filter">
                <IconButton
                  size="small"
                  onClick={onClearMissingEmailFilter}
                  disabled={actionsDisabled}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      )}

      <input
        id="unit-import-input"
        type="file"
        accept=".csv,.xlsx,.xls"
        onChange={handleImportFile}
        style={{ display: "none" }}
      />

      {unitsLoading ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Loading units...
        </Typography>
      ) : units?.length ? (
        unitsTotal > unitsPageSize ? (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Unit</TableCell>
                  <TableCell>Owner</TableCell>
                  <TableCell>Contact</TableCell>
                  {canDelete && <TableCell align="right">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {units.map((unit) => (
                  <TableRow
                    key={unit.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => navigate(`/units/${unit.id}/details`)}
                  >
                    <TableCell>
                      {unit.unit_name || unit.unit_code || `Unit ${unit.id}`}
                    </TableCell>
                    <TableCell>{unit.owner_name || "N/A"}</TableCell>
                    <TableCell>
                      {unit.email || unit.mobile ? (
                        <a
                          href={
                            unit.email
                              ? `mailto:${formatMailto(unit.email)}`
                              : `tel:${unit.mobile}`
                          }
                          onClick={(e) => e.stopPropagation()}
                          style={{ color: "inherit" }}
                        >
                          {unit.email || unit.mobile}
                        </a>
                      ) : (
                        "N/A"
                      )}
                    </TableCell>
                    {canDelete && (
                      <TableCell align="right">
                        {!lpe1UnitIdSet.has(unit.id) && (
                          <IconButton
                            size="small"
                            aria-label="Delete unit"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteUnit?.(unit.id);
                            }}
                            disabled={actionsDisabled}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Grid container spacing={3}>
            {units.map((unit) => (
              <Grid item xs={12} sm={6} md={4} key={unit.id}>
                <Paper
                  sx={{ p: 2.5, borderRadius: 2, cursor: "pointer", position: "relative" }}
                  onClick={() => navigate(`/units/${unit.id}/details`)}
                >
                  {canDelete && !lpe1UnitIdSet.has(unit.id) && (
                    <IconButton
                      size="small"
                      aria-label="Delete unit"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteUnit?.(unit.id);
                      }}
                      disabled={actionsDisabled}
                      sx={{ position: "absolute", top: 8, right: 8 }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                  <Typography fontWeight={600}>
                    {unit.unit_name || unit.unit_code || `Unit ${unit.id}`}
                  </Typography>
                  <Typography variant="body2">
                    Owner: {unit.owner_name || "N/A"}
                  </Typography>
                  {(unit.email || unit.mobile) && (
                    <Typography variant="body2">
                      {unit.email ? "Email: " : "Phone: "}
                      <a
                        href={
                          unit.email
                            ? `mailto:${formatMailto(unit.email)}`
                            : `tel:${unit.mobile}`
                        }
                        onClick={(e) => e.stopPropagation()}
                        style={{ color: "inherit" }}
                      >
                        {unit.email || unit.mobile}
                      </a>
                    </Typography>
                  )}
                </Paper>
              </Grid>
            ))}
          </Grid>
        )
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          
        </Typography>
      )}
      {unitsTotal > unitsPageSize && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Pagination
            count={Math.ceil(unitsTotal / unitsPageSize)}
            page={unitsPage}
            onChange={onUnitsPageChange}
            color="primary"
            size="small"
          />
        </Box>
      )}
    </Paper>
  );
}

function SectionLPE1({
  lpe1Forms,
  lpe1Page,
  lpe1Total,
  lpe1PageSize,
  lpe1Loading,
  lpe1Error,
  onLpe1PageChange,
  navigate,
  location,
  formatDate,
}) {
  return (
    <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 4 }}>
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
        Property Enquiries
        {lpe1Total > 0 && (
          <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            ({lpe1Total} total)
          </Typography>
        )}
      </Typography>

      {lpe1Error ? (
        <Alert severity="error">{lpe1Error}</Alert>
      ) : lpe1Loading ? (
        <Typography>Loading property enquiries...</Typography>
      ) : lpe1Forms.length > 0 ? (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Last Updated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lpe1Forms.map((f) => {
                  const formType = f.form_type || "LPE1";
                  const routePrefix = formType.toLowerCase() === "fme1" ? "fme1" : "lpe1";
                  return (
                    <TableRow
                      key={`${formType}-${f.id}`}
                      hover
                      onClick={() => navigate(`/${routePrefix}/${f.id}`, { state: { from: location.pathname } })}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>{formType}</TableCell>
                      <TableCell>{f.unit_name || `Unit ${f.unit_id}`}</TableCell>
                      <TableCell>{formatDate(f.created_at_utc)}</TableCell>
                      <TableCell>{formatDate(f.updated_at_utc)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {lpe1Total > lpe1PageSize && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Pagination
                count={Math.ceil(lpe1Total / lpe1PageSize)}
                page={lpe1Page}
                onChange={onLpe1PageChange}
                color="primary"
                size="small"
              />
            </Box>
          )}
        </>
      ) : (
        <Typography>No property enquiries found.</Typography>
      )}
    </Paper>
  );
}

function SectionTickets({
  tickets,
  ticketsPage,
  ticketsTotal,
  ticketsPageSize,
  onTicketsPageChange,
  propertyId,
  showResolvedClosed,
  onToggleResolvedClosed,
  ticketsError,
  ticketsLoading,
  canCreate,
  onCreateTicket,
  actionsDisabled = false,
}) {
  const navigate = useNavigate();
  const [hoverComments, setHoverComments] = useState({});
  const [hoverLoading, setHoverLoading] = useState({});
  const priorityColors = {
    Low: "#4caf50",
    Medium: "#ff9800",
    High: "#f44336",
    Urgent: "#7b1fa2",
  };

  const formatRelativeTime = (dateValue) => {
    if (!dateValue) return "-";
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return "-";
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (seconds < 60) return "just now";
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} min${minutes === 1 ? "" : "s"} ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months} month${months === 1 ? "" : "s"} ago`;
    const years = Math.floor(months / 12);
    return `${years} year${years === 1 ? "" : "s"} ago`;
  };

  const ensureHoverComments = async (ticketId) => {
    if (hoverComments[ticketId] || hoverLoading[ticketId]) {
      return;
    }
    setHoverLoading((prev) => ({ ...prev, [ticketId]: true }));
    try {
      const data = await getTicketComments(ticketId);
      const ordered = (data || []).slice().sort((a, b) => {
        return new Date(b.created_at_utc) - new Date(a.created_at_utc);
      });
      setHoverComments((prev) => ({ ...prev, [ticketId]: ordered }));
    } catch (err) {
      console.error("Failed to load ticket comments:", err);
    } finally {
      setHoverLoading((prev) => ({ ...prev, [ticketId]: false }));
    }
  };

  const renderPriorityDot = (priority) => {
    const color = priorityColors[priority] || "#9e9e9e";
    return (
      <Tooltip title={priority || "Unknown"} placement="top">
        <span
          style={{
            display: "inline-block",
            width: 10,
            height: 10,
            borderRadius: "50%",
            backgroundColor: color,
          }}
        />
      </Tooltip>
    );
  };

  return (
    <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 4 }}>
      <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={600}>Tickets</Typography>
        <Stack direction="row" spacing={1.5} alignItems="center">
          {canCreate && (
            <Button
              variant="contained"
              size="small"
              onClick={onCreateTicket}
              disabled={actionsDisabled}
            >
              + Create Ticket
            </Button>
          )}
          <FormControlLabel
            control={
              <Switch
                checked={showResolvedClosed}
                onChange={onToggleResolvedClosed}
              />
            }
            label="Show resolved & closed"
          />
        </Stack>
      </Grid>

      {ticketsError ? (
        <Typography color="error">{ticketsError}</Typography>
      ) : ticketsLoading ? (
        <Typography>Loading tickets...</Typography>
      ) : tickets.length > 0 ? (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Assigned</TableCell>
                  <TableCell>Latest Comment</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tickets.map((ticket) => (
                  <TableRow key={ticket.id}>
                    <TableCell>
                      <Button
                        size="small"
                        onClick={() => navigate(`/tickets?ticketId=${ticket.id}`)}
                        sx={{ textTransform: "none", justifyContent: "flex-start" }}
                      >
                        {ticket.title}
                      </Button>
                    </TableCell>
                    <TableCell>{renderPriorityDot(ticket.priority)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={ticket.status}
                        color={ticket.status === "Open" ? "warning" : "default"}
                      />
                    </TableCell>
                    <TableCell>{ticket.assigned_to_name || "Unassigned"}</TableCell>
                    <TableCell>
                      {ticket.latest_comment_at ? (
                        <Tooltip
                          title={
                            hoverComments[ticket.id]?.length ? (
                              <Box sx={{ maxWidth: 320 }}>
                                {hoverComments[ticket.id].map((comment) => (
                                  <Box key={comment.id} sx={{ mb: 1.5 }}>
                                    <Typography variant="caption" color="text.secondary">
                                      {comment.created_by_email ||
                                        comment.created_by_name ||
                                        "Unknown"}{" "}
                                      • {formatRelativeTime(comment.created_at_utc)}
                                    </Typography>
                                    <Typography variant="body2">
                                      {comment.comment_text}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            ) : hoverLoading[ticket.id] ? (
                              "Loading comments..."
                            ) : (
                              "No comments"
                            )
                          }
                          placement="top-start"
                          onOpen={() => ensureHoverComments(ticket.id)}
                        >
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                            <InfoOutlinedIcon fontSize="small" />
                            {formatRelativeTime(ticket.latest_comment_at)}
                          </span>
                        </Tooltip>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {ticketsTotal > ticketsPageSize && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Pagination
                count={Math.ceil(ticketsTotal / ticketsPageSize)}
                page={ticketsPage}
                onChange={onTicketsPageChange}
                color="primary"
                size="small"
              />
            </Box>
          )}
        </>
      ) : (
        <Typography>No tickets found.</Typography>
      )}
    </Paper>
  );
}

function SectionCard({
  title,
  items,
  canManage,
  actionsDisabled = false,
  manageLink,
  columns,
  dateField,
  statusResolver,
  loading = false,
  error = "",
  page = 1,
  total = 0,
  pageSize = 20,
  onPageChange,
}) {
  const navigate = useNavigate();

  return (
    <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 4 }}>
      <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={600}>{title}</Typography>
        {canManage && (
          <Button
            variant="outlined"
            onClick={() => navigate(manageLink)}
            disabled={actionsDisabled}
          >
            Manage
          </Button>
        )}
      </Grid>

      {error ? (
        <Alert severity="error">{error}</Alert>
      ) : loading ? (
        <Typography>Loading {title.toLowerCase()}...</Typography>
      ) : items?.length ? (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {columns.map(([label]) => (
                    <TableCell key={label}>{label}</TableCell>
                  ))}
                  {dateField && <TableCell>Status</TableCell>}
                </TableRow>
              </TableHead>
                <TableBody>
                {items.map((row) => {
                  const docStatus = dateField
                    ? (statusResolver ? statusResolver(row) : getDocumentStatus(row[dateField]))
                    : null;
                  
                  return (
                    <TableRow key={row.id}>
                      {columns.map(([_, field], i) => (
                        <TableCell key={i}>
                          {typeof field === "function" ? field(row) : row[field]}
                        </TableCell>
                      ))}
                      {dateField && (
                        <TableCell>
                          <Chip
                            icon={docStatus.icon}
                            label={docStatus.label}
                            color={docStatus.color}
                            size="small"
                            variant={docStatus.status === "valid" ? "outlined" : "filled"}
                          />
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {onPageChange && total > pageSize && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Pagination
                count={Math.ceil(total / pageSize)}
                page={page}
                onChange={onPageChange}
                color="primary"
                size="small"
              />
            </Box>
          )}
        </>
      ) : (
        <Typography>No {title.toLowerCase()}.</Typography>
      )}
    </Paper>
  );
}

function TabPanel({ value, index, children }) {
  const isActive = value === index;
  return (
    <Box role="tabpanel" hidden={!isActive} sx={{ display: isActive ? "block" : "none" }}>
      {isActive ? children : null}
    </Box>
  );
}

export default PropertyDetailsPage;
